import React, { useEffect, useState, useMemo, useRef } from 'react';
import { ProgressStepBar } from '../CreditApplicationComponents/ProgressStepBar';
import StepFormButtons from '../CreditApplicationComponents/StepFormButtons';
import {
  AddressVerification,
  BankReference,
  CreditApplicationTemplate,
  DynamicInvitedSignaturesFields,
  GroupedCreditApplicationFields,
  ConditionalOptionValues,
  CreditApplication,
  renderTemplate,
  toCreditApplicationType,
  blockedBankReferenceEmailMessage,
  blockedTradeReferenceEmailMessage,
  findBlockedBankReferenceEmail,
  findBlockedTradeReferenceEmailDomain,
  getBlockedTradeReferenceEmailDomains,
  isFieldRequiredForAnswers,
  getCreditApplicationTypeLabel,
  isFieldShownForAnswers,
} from '@dill/dill-shared';
import DynamicStepForm from '../CreditApplicationComponents/DynamicStepForm';
import { verifyAddressFieldsOnSubmit } from '../CreditApplicationComponents/verifyAddressFieldsOnSubmit';
import { deriveOptionState } from '../CreditApplicationComponents/conditionalOptionUtils';
import {
  PreviewCreditApplicationData,
  PreviewCreditApplicationRef,
} from '../CreditApplicationComponents/PreviewCreditApplicationData';
import { firebaseLargeFileUploadHelper, isHTML, openDillToast } from '../../../../utils/helpers';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  createApplicationUpdateHistory,
  createCreditApplication,
  getCreditApplication,
  getCustomerApplications,
  saveDraftCreditApplication,
  updateCreditApplication,
} from '../../../../redux/services/creditApplicationsService';

import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import CryptoJS from 'crypto-js';
import {
  checkFirebaseUser,
  createCreditAppFirebaseUser,
  firebaseConfirmLoginUser,
  firebaseSignInWithCustomToken,
  unverifyFirebaseUserAndSendLink,
} from '../../../../redux/services/authService';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';
import moment from 'moment';
import { ConditionalOptionPreviewRef } from '../CreditApplicationComponents/ConditionalOptionPreview';
import * as Sentry from '@sentry/react';
import { myFirebaseAuth } from '../../../../firebase/config';
import CustomerSignatureModal from '../CreditApplicationComponents/CustomerSignatureModal';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import { CustomerApplicationDashboard } from './CustomerApplicationDashboard';

enum ModalState {
  ADD_CREDIT_APPLICATION,
  SUCCESS_SAVING_CREDIT_APPLICATION,
}
type StateInvitedUser = {
  title: string;
  email: string;
  step: number;
  signatureGroupName?: string;
  placeHolder?: string;
  type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  minimumCreditAmount?: number;
  fields: DynamicInvitedSignaturesFields[];
  minimumNumberOfSignatures?: number;
  linkedFieldId?: string | null;
  id?: string | null;
  signaturePageFieldIds?: string | null;
  signature?: string;
  dateSigned?: string;
  ipAddress?: string;
  location?: string;
};

type AllStepFieldValue = {
  uniqueId: string;
  id: string;
  name: string;
  fromField:
    | 'TEXT'
    | 'TEXT_AREA'
    | 'NUMBER'
    | 'CURRENCY'
    | 'DATE'
    | 'CHECK_BOX'
    | 'OPTION_FIELD'
    | 'FILE'
    | 'DOCUMENT_FIELD'
    | 'INVITE_USER'
    | 'DATE'
    | 'CONDITIONAL_OPTION'
    | 'BANK_REFERENCE'
    | 'TRADE_REFERENCE'
    | 'CONDITIONAL_SELECTED_FIELD'
    | 'STATE_DROPDOWN'
    | 'CHECKBOX_LIST'
    | 'CONDITIONAL_FORM'
    | 'DYNAMIC_FORM_DOCUMENT'
    | 'AUTOFILL_CHECKBOX'
    | 'INTERNAL_SIGNER'
    | 'DROPDOWN';
  isRequired: boolean;
  isFieldEmail: boolean;
  isHashed: boolean;
  stepName: string;
  stepNumber: number;
  index: number;
  groupStepPosition: number;
  groupName: string;
  groupColumnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
  isFieldArray: boolean;
  groupedPosition: number;
  value: string | number | boolean | File | any | null;
  optionType?: string;
  secondaryText?: string;
  status?:
    | 'Pending'
    | 'Requested'
    | 'Submitted'
    | 'Rejected'
    | 'Blocked'
    | 'Dropped'
    | 'Delivered'
    | 'Deferred'
    | 'Bounce';
  emailDate?: string;
  sendEmail?: boolean;
  type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  secondaryValue?: string | null;
  fieldValue?: ConditionalOptionValues[];
  onlyShowPlaidOption?: boolean;
  showIfNotPlaid?: boolean;
  formTemplate?: string | null;
  linkedFieldId?: string | null;
  populateFieldSignatureId?: string | null;
  autoPopulateFieldIds?: string | null;
  copyFromFieldIds?: string | null;
  copyToFieldIds?: string | null;
  conditionalVisibility?: { sourceFieldId: string; sourceOptionValues: string[] } | null;
  conditionalRequired?: { sourceFieldId: string; sourceOptionValues: string[] } | null;
  datePickerFormat?: 'FULL_DATE' | 'MONTH_YEAR';
  validationType?: 'NONE' | 'EIN' | 'SSN' | 'PHONE';
  emailValidationType?: 'SINGLE' | 'MULTIPLE';
  maxLength?: number;
  currencyDecimalPlaces?: number;
  addressVerification?: AddressVerification | null;
  borderId?: string | null;
};

export const CreditApplicationRequestForm = ({
  domainDetails,
  availableTemplates,
  selectedTemplate,
  onSelectTemplate,
}: {
  domainDetails: {
    id: string;
    domainId: string;
    url: string;
    name: string;
    branches: { name: string; erpId?: string }[];
    salesReps?: { name: string; erpId?: string }[];
  } | null;
  availableTemplates: CreditApplicationTemplate[];
  selectedTemplate: CreditApplicationTemplate | null;
  onSelectTemplate: (template: CreditApplicationTemplate) => void;
}) => {
  const dispatch = useAppDispatch();
  const parentRef = useRef<ConditionalOptionPreviewRef>(null);
  const previewRef = useRef<PreviewCreditApplicationRef>(null);
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState<string[]>([]);
  const [userFirstName, setUserFirstName] = useState<string>('');
  const [userLastName, setUserLastName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [salesRepId, setSalesRepId] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [modalState, setModalState] = useState(ModalState.ADD_CREDIT_APPLICATION);
  const [modalDetails, setModalDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  }>({
    modalType: 'Success',
    title: '',
    details: '',
  });
  const [editableSelectedTemplate, setEditableSelectedTemplate] =
    useState<CreditApplicationTemplate | null>(null);
  const [stepMode, setStepMode] = useState<
    'STEP_DETAILS' | 'EMAIL_LINK_SENT' | 'APPLICATION_DASHBOARD'
  >('STEP_DETAILS');
  const [customerApplications, setCustomerApplications] = useState<CreditApplication[]>([]);
  const customerApplicationsRef = useRef<CreditApplication[]>([]);
  const nextStep = () => setStep((prev) => Math.min(prev + 1, steps.length - 1));
  const [errors, setErrors] = useState<
    {
      uniqueId: string;
      errorText: string;
    }[]
  >([]);
  const [hasConditionalFieldErrors, setHasConditionalFieldErrors] = useState(false);
  const [allStepFieldsValues, setAllStepFieldsValues] = useState<AllStepFieldValue[]>([]);
  const [invitedSignatures, setInvitedSignatures] = useState<StateInvitedUser[]>([]);
  const [originalInvitedSignatures, setOriginalInvitedSignatures] = useState<StateInvitedUser[]>(
    []
  );
  const [invitedSignaturesErrors, setInvitedSignaturesErrors] = useState<
    { placeHolder: string; errorText: string }[]
  >([]);
  const [bankReference, setBankReference] = useState<BankReference[] | null>(null);
  const [bankError, setBankError] = useState<string | null>(null);
  const [creditApplicationId, setCreditApplicationId] = useState<string | null>(null);
  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 0));
    setErrors([]);
    setInvitedSignaturesErrors((prev) => prev.map((e) => ({ ...e, errorText: '' })));
    setBankError(null);
    setHasConditionalFieldErrors(false);
  };
  const [senderEmail, setSenderEmail] = useState<string | null>(null);
  const creditApplicationIdRef = useRef<string | null>(null);
  const applicationUpdateIdRef = useRef<string | null>(null);
  const [openCustomerSignatureModal, setOpenCustomerSignatureModal] = useState<boolean>(false);
  const [signature, setSignature] = useState('');
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [openSignNowModal, setOpenSignNowModal] = useState(false);
  const [signNowFieldData, setSignNowFieldData] = useState<{
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  } | null>(null);
  const [signedNowPlaceholders, setSignedNowPlaceholders] = useState<string[]>([]);
  // Slots signed via "Sign Now" are persisted on the draft, but signedNowPlaceholders is
  // local state — after a refresh the restored signatures would otherwise be treated as
  // unsigned and block the step with "please use Sign Now". Rebuild it from restored data.
  useEffect(() => {
    const signedPlaceholders = invitedSignatures
      .filter((sig) => sig.signature && sig.placeHolder)
      .map((sig) => sig.placeHolder as string);
    if (signedPlaceholders.length === 0) return;
    setSignedNowPlaceholders((prev) => {
      const missing = signedPlaceholders.filter((p) => !prev.includes(p));
      return missing.length > 0 ? [...prev, ...missing] : prev;
    });
  }, [invitedSignatures]);
  const [ipdData, setIpData] = useState<{ ipAddress: string; location: string } | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const fromUrlParamsRef = useRef<boolean>(false);
  const urlAppStatusRef = useRef<string | null>(null);
  const urlSourceApplicationIdRef = useRef<string | null>(null);
  const [draftCreditApplication, setDraftedCreditApplication] = useState<CreditApplication | null>(
    null
  );
  const [prefillDraftApplication, setPrefillDraftApplication] = useState<CreditApplication | null>(
    null
  );

  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const { basicSupplier } = useAppSelector((state) => state.buyers);
  const { decryptValue } = useDecryption();
  const updatedFieldsRef = useRef<AllStepFieldValue[] | null>(null);

  const setUpdatedFieldsRef = (vals: AllStepFieldValue[]) => {
    updatedFieldsRef.current = vals;
  };

  const handleTemplateSelect = (template: CreditApplicationTemplate) => {
    onSelectTemplate(template);
    setEditableSelectedTemplate(template);
    setStep(0);
    setErrors([]);
  };

  const extractInviteUsers = (
    indx: number,
    whoNeedsToSign?: string,
    name?: string,
    type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR',
    minimumCreditAmount?: number,
    fields?: DynamicInvitedSignaturesFields[],
    minimumNumberOfSignatures?: number,
    isRequired?: boolean,
    linkedFieldId?: string | null,
    id?: string,
    signaturePageFieldIds?: string | null
  ): {
    title: string;
    email: string;
    step: number;
    signatureGroupName?: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    minimumCreditAmount?: number;
    fields: DynamicInvitedSignaturesFields[];
    minimumNumberOfSignatures?: number;
    isRequired?: boolean;
    linkedFieldId?: string;
    id?: string;
    signaturePageFieldIds?: string | null;
  }[] => {
    if (!whoNeedsToSign) return [];
    return whoNeedsToSign.split(',').map((option) => ({
      title: '',
      email: '',
      step: indx,
      signatureGroupName: name,
      placeHolder: option.trim(),
      type: type ?? 'AUTHORIZED_AGENT',
      minimumCreditAmount,
      fields: fields || [],
      minimumNumberOfSignatures,
      isRequired,
      id,
      ...(linkedFieldId ? { linkedFieldId } : {}),
      ...(signaturePageFieldIds ? { signaturePageFieldIds } : {}),
    }));
  };

  const applicationTitle = useMemo(() => {
    if (editableSelectedTemplate) {
      return getCreditApplicationTypeLabel(editableSelectedTemplate.applicationType, {
        long: true,
        formName: editableSelectedTemplate.name,
      });
    }
    return ' Application';
  }, [editableSelectedTemplate]);

  useEffect(() => {
    async function fetchApiData() {
      try {
        const ipRes = await fetch('https://free.freeipapi.com/api/json/');
        const ipData = await ipRes.json();

        setIpData({
          ipAddress: ipData.ipAddress,
          location: `${ipData.cityName}, ${ipData.regionName}, ${ipData.countryName}`,
        });
      } catch (err) {
        console.error(err);
      }
    }

    fetchApiData();
  }, []);

  useEffect(() => {
    if (!editableSelectedTemplate) return;
    const inviteData: {
      title: string;
      email: string;
      step: number;
      fullNameNeeded?: boolean;
      driverLicenseNeeded?: boolean;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
      linkedFieldId?: string;
    }[] = [];

    editableSelectedTemplate.steps.forEach((stp, index) => {
      stp.fields?.forEach((field) => {
        if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
          field.unGroupedFields.forEach((f) => {
            if (f.fromField === 'INVITE_USER') {
              inviteData.push(
                ...extractInviteUsers(
                  index,
                  f.whoNeedsToSign,
                  f.name,
                  f.type,
                  f.minimumCreditAmount,
                  f.invitedSignaturesFields || [],
                  f.minimumNumberOfSignatures,
                  f.isRequired,
                  f?.linkedFieldId,
                  f?.id,
                  f.signaturePageFieldIds
                )
              );
            }
          });
        }

        if (field.stepType === 'GROUPED' && field.groupedFields) {
          field.groupedFields.forEach((group) => {
            group.fields.forEach((f) => {
              if (f.fromField === 'INVITE_USER') {
                inviteData.push(
                  ...extractInviteUsers(
                    index,
                    f.whoNeedsToSign,
                    f.name,
                    f.type,
                    f.minimumCreditAmount,
                    f.invitedSignaturesFields || [],
                    f.minimumNumberOfSignatures,
                    f.isRequired,
                    f?.linkedFieldId,
                    f?.id,
                    f.signaturePageFieldIds
                  )
                );
              }
            });
          });
        }
      });
    });

    setInvitedSignatures(inviteData);
    setOriginalInvitedSignatures(inviteData);
    setInvitedSignaturesErrors(
      inviteData.map((item) => ({ placeHolder: item?.placeHolder ?? '', errorText: '' }))
    );
  }, [editableSelectedTemplate]);

  useEffect(() => {
    if (!creditApplication) return;

    const { status } = creditApplication;

    if (status === 'SENT' && creditApplicationId) {
      dispatch(
        updateCreditApplication({
          creditApplicationId,
          status: 'OPENED',
          // This is a fire-and-forget status ping, not a save of the applicant's in-progress
          // answers. Its response reflects the document as it was in the DB when this request was
          // read — not whatever the applicant has typed locally since — so it must not overwrite
          // state.creditApplication and re-trigger the form's decrypt/re-sync effects with that
          // stale snapshot.
          skipCreditApplicationSync: true,
        })
      );
      return;
    }

    if (!['SENT', 'OPENED', 'DRAFT'].includes(status) && !senderEmail) {
      setCreditApplicationId(null);
    }
  }, [creditApplication, creditApplicationId]);

  useEffect(() => {
    if (creditApplicationId && creditApplicationId !== creditApplicationIdRef.current) {
      creditApplicationIdRef.current = creditApplicationId;
      dispatch(
        getCreditApplication({ creditApplicationId: creditApplicationId, hashedType: 'encrypted' })
      );
    }
  }, [creditApplicationId]);

  const getFirebaseConfirmLoginUser = async (url: string, email: string, action?: string) => {
    const result = await dispatch(firebaseConfirmLoginUser({ url }));
    if (result.type === 'auth/firebaseConfirmLoginUser/fulfilled') {
      const decodedUrl = decodeURIComponent(url);
      const parsedUrl = new URL(decodedUrl);

      const domainId = parsedUrl.searchParams.get('domainId');
      const templateId = parsedUrl.searchParams.get('templateId');
      const salesRepIdParam = parsedUrl.searchParams.get('salesRepId');
      const branchIdParam = parsedUrl.searchParams.get('branchId');
      const urlApplicationId = parsedUrl.searchParams.get('applicationId');

      const cleanUrl = new URL(window.location.origin + window.location.pathname);
      if (domainId) cleanUrl.searchParams.set('domainId', domainId);
      if (templateId) cleanUrl.searchParams.set('templateId', templateId);
      if (salesRepIdParam) cleanUrl.searchParams.set('salesRepId', salesRepIdParam);
      if (branchIdParam) cleanUrl.searchParams.set('branchId', branchIdParam);

      window.history.replaceState({}, document.title, cleanUrl.toString());
      let applications: CreditApplication[] = [];

      if (!urlApplicationId && !fromUrlParamsRef.current && email) {
        const applicationsResult = await dispatch(
          getCustomerApplications({
            email,
            isArchived: false,
          })
        );
        if (applicationsResult.type === 'creditApplications/getCustomerApplications/fulfilled') {
          applications = (applicationsResult as any).payload?.data || [];
          customerApplicationsRef.current = applications;
          setCustomerApplications(applications);
          if (applications.length > 0 && process.env.REACT_APP_ENVIRONMENT === 'production') {
            setStepMode('APPLICATION_DASHBOARD');
            return;
          }
        }
      }
      const currentSupplierId = domainDetails?.id || editableSelectedTemplate?.supplierId || '';
      const byUpdatedAt = (a: CreditApplication, b: CreditApplication) =>
        new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime();

      const supplierDraft = [...applications]
        .filter(
          (app: CreditApplication) => app.supplierId === currentSupplierId && app.status === 'DRAFT'
        )
        .sort(byUpdatedAt)[0];

      if (supplierDraft) {
        // Same-supplier draft: continue it directly
        setCreditApplicationId(supplierDraft.id);
        setSenderEmail(supplierDraft.senderEmail);
        setDraftedCreditApplication(supplierDraft);
        setStepMode('STEP_DETAILS');
        setStep(1);
      } else {
        const otherSupplierDraft =
          [...applications]
            .filter(
              (app: CreditApplication) =>
                app.supplierId !== currentSupplierId && app.status === 'DRAFT'
            )
            .sort(byUpdatedAt)[0] ?? null;

        applicationUpdateIdRef.current = null;
        setCreditApplicationId(null);
        setSenderEmail(null);
        // Prefill fieldValue, bankReference and invitedSignatures from other-supplier draft
        setPrefillDraftApplication(otherSupplierDraft);
        setStepMode('STEP_DETAILS');
        setStep(1);
      }

      setStepMode('STEP_DETAILS');
      setStep(1);
    } else {
      if (action === 'goBack') {
        setStepMode('STEP_DETAILS');
        setStep(0);
      } else {
        setStepMode('EMAIL_LINK_SENT');
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlModeValue = params.get('mode');
    const apiKey = params.get('apiKey');
    const firstName = params.get('firstName');
    const lastName = params.get('lastName');
    const applicationId = params.get('applicationId');
    const senderEmailParam = params.get('senderEmail');
    const redirectedParam = params.get('redirected');
    const salesRepIdParam = params.get('salesRepId');
    const branchIdParam = params.get('branchId');

    if (firstName) {
      setUserFirstName(firstName);
    }
    if (lastName) {
      setUserLastName(lastName);
    }
    if (senderEmailParam) {
      setSenderEmail(senderEmailParam);
    }
    if (salesRepIdParam) {
      setSalesRepId(salesRepIdParam);
    }
    if (branchIdParam) {
      setBranchId(branchIdParam);
    }
    if (applicationId && senderEmailParam) {
      fromUrlParamsRef.current = true;
    }

    if (redirectedParam) {
      const emailForSignIn = localStorage.getItem('emailForSignIn');
      if (emailForSignIn) {
        setUserEmail(emailForSignIn);
        getFirebaseConfirmLoginUser(window.location.href, emailForSignIn);
        return;
      }
    }

    if ((urlModeValue === '' || urlModeValue === null) && apiKey) {
      const emailForSignIn = localStorage.getItem('emailForSignIn');
      const confirmedEmail = emailForSignIn || senderEmailParam || '';
      if (emailForSignIn) {
        setUserEmail(emailForSignIn);
      }
      getFirebaseConfirmLoginUser(window.location.href, confirmedEmail);
    }
    if (urlModeValue === 'signIn' && apiKey) {
      localStorage.setItem('newSignIn', 'newSignIn');
      const emailForSignIn = localStorage.getItem('emailForSignIn');
      if (emailForSignIn) {
        setUserEmail(emailForSignIn);
        getFirebaseConfirmLoginUser(window.location.href, emailForSignIn, 'goBack');
      } else {
        setStepMode('STEP_DETAILS');
        setStep(0);
        openDillToast({
          type: 'ERROR',
          message:
            'Failed to verify your email. You might have opened the verification link for a different browser. You can resend a verification email for this browser by entering your email below or open the verification link with the previous browser',
        });
      }
    }

    // Handle applicationId from URL - use draft-finding logic similar to handleContinueApplication
    if (applicationId) {
      const loadApplicationFromUrl = async () => {
        try {
          setLoading(true);
          const result = await dispatch(
            getCreditApplication({
              creditApplicationId: applicationId,
              hashedType: 'encrypted',
            })
          );
          if (result.type === 'creditApplications/getCreditApplication/fulfilled') {
            const app = (result as any).payload?.data;
            if (app) {
              const { status, supplierId } = app;
              urlAppStatusRef.current = status;
              urlSourceApplicationIdRef.current = applicationId;
              // Always populate form fields from the URL app's data so nothing is missing
              setDraftedCreditApplication(app);

              // Non-editable statuses - navigate to form without loading the application
              if (!['SENT', 'OPENED', 'DRAFT'].includes(status)) {
                setStepMode('STEP_DETAILS');
                setStep(0);
                return;
              }

              // The URL application is always the submission target.
              // Pre-set the ref so the creditApplicationId effect skips its
              // redundant getCreditApplication call (preventing a double OPENED update).
              creditApplicationIdRef.current = applicationId;
              setCreditApplicationId(applicationId);
              setSenderEmail(app.senderEmail);
              setStepMode('STEP_DETAILS');

              const isConfirmingSignIn = !!apiKey || !!redirectedParam;
              if (!isConfirmingSignIn) {
                const currentUser = myFirebaseAuth.currentUser;
                const isUserVerified =
                  !!currentUser &&
                  currentUser.email?.toLowerCase() === app.senderEmail?.toLowerCase() &&
                  currentUser.emailVerified;
                if (!isUserVerified) {
                  setUserEmail(app.senderEmail);
                }
                setStep(isUserVerified ? 1 : 0);
              }

              if (status === 'SENT' || status === 'OPENED') {
                // If drafts are already loaded, override with draft data (same supplier first, then any)
                const loadedApps = customerApplicationsRef.current;
                if (loadedApps.length > 0) {
                  const drafts = loadedApps.filter((a) => a.status === 'DRAFT');
                  const byUpdatedAt = (a: CreditApplication, b: CreditApplication) =>
                    new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
                  const supplierDraft = [...drafts]
                    .filter((a) => a.supplierId === supplierId)
                    .sort(byUpdatedAt)[0];
                  const anyDraft = [...drafts].sort(byUpdatedAt)[0];
                  const draft = supplierDraft ?? anyDraft;

                  if (draft) {
                    setDraftedCreditApplication(draft);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading application from URL:', error);
        } finally {
          setLoading(false);
        }
      };

      loadApplicationFromUrl();
    }
  }, [location.search, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateWithNextGroupedPosition = (allFields: GroupedCreditApplicationFields[]) => {
    const groupedFields = allFields.filter((f) => f.groupedPosition !== undefined);

    // Find the current max grouped position
    const maxGroupPos = Math.max(...groupedFields.map((f) => f.groupedPosition ?? 0));

    // Get only fields of the last group
    const lastGroupFields = groupedFields.filter((f) => f.groupedPosition === maxGroupPos);

    const nextGroupedPosition = maxGroupPos + 1;

    const newGroup = lastGroupFields.map((field) => {
      // Strip existing `-group-X` suffix
      const cleanFieldId = field.id.replace(/-group-\d+$/, '');
      const clonedFields = field.fields.map((f) => {
        const cleanId = f.id.replace(/-group-\d+$/, '');
        return {
          ...f,
          id: `${cleanId}-group-${nextGroupedPosition}`,
        };
      });

      return {
        ...field,
        id: `${cleanFieldId}-group-${nextGroupedPosition}`,
        groupedPosition: nextGroupedPosition,
        fields: clonedFields,
      };
    });

    return [...allFields, ...newGroup];
  };

  useEffect(() => {
    setEditableSelectedTemplate(() => {
      if (!selectedTemplate) return null;
      const specificRules = creditApplication?.specificRules ?? [];
      const updatedTemplate = {
        ...selectedTemplate,
        steps: selectedTemplate.steps.map((stepItem) => ({
          ...stepItem,
          fields: stepItem.fields.map((field) => {
            if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
              const overriddenFields = field.unGroupedFields.map((f) => {
                const rule = specificRules.find((r) => r.fieldId === f.id);
                if (!rule) return f;
                return {
                  ...f,
                  ...(rule.isRequired !== undefined ? { isRequired: rule.isRequired } : {}),
                  ...(f.fromField === 'INVITE_USER' && rule.minimumNumberOfSignatures !== undefined
                    ? { minimumNumberOfSignatures: rule.minimumNumberOfSignatures }
                    : {}),
                  ...(f.fromField === 'INVITE_USER' && rule.minimumCreditAmount !== undefined
                    ? { minimumCreditAmount: rule.minimumCreditAmount }
                    : {}),
                  ...(rule.hidden !== undefined
                    ? {
                        hidden: rule.hidden,
                        conditionalVisibility: rule.hidden
                          ? { sourceFieldId: '__specific_rule_hidden__', sourceOptionValues: [] }
                          : null,
                      }
                    : {}),
                };
              });
              return { ...field, unGroupedFields: overriddenFields };
            }

            if (
              field.stepType === 'GROUPED' &&
              field.minimunNumberOfGroups &&
              field.groupedFields
            ) {
              const groupRule = specificRules.find((r) => r.fieldId === `group:${field.groupName}`);
              const currentGroupCount = Math.max(
                ...field.groupedFields.map((item) => item.groupedPosition ?? 0)
              );
              let minimumGroups = field.minimunNumberOfGroups - 1;
              if (groupRule?.minimumNumberOfGroups !== undefined) {
                minimumGroups = groupRule.minimumNumberOfGroups - 1;
                if (groupRule.minimumNumberOfGroups === 0) {
                  return { ...field, groupedFields: [] };
                }
              } else if (
                creditApplication?.groupedSpecialConditions &&
                creditApplication?.groupedSpecialConditions.length > 0
              ) {
                const condition = creditApplication?.groupedSpecialConditions[0].minimumNumber;
                minimumGroups = condition - 1;
                if (condition === 0) {
                  return {
                    ...field,
                    groupedFields: [],
                  };
                }
              }

              // If we have fewer groups than the minimum, add the missing ones
              if (currentGroupCount < minimumGroups) {
                const missingGroups = minimumGroups - currentGroupCount;
                let updatedGroupedFields = [...field.groupedFields];

                // Add the missing groups using the handleAddGroupedField logic
                for (let i = 0; i < missingGroups; i++) {
                  updatedGroupedFields = duplicateWithNextGroupedPosition(updatedGroupedFields);
                }

                return {
                  ...field,
                  groupedFields: updatedGroupedFields,
                };
              }
            }
            return field;
          }),
        })),
      };
      // A bank reference the supplier removed via Change Requirements is taken out of the
      // application entirely (not just hidden), so it isn't rendered, validated or submitted, and
      // a step left with nothing on it is dropped.
      const isRemovedBankReference = (f: { id: string; fromField: string }) =>
        f.fromField === 'BANK_REFERENCE' &&
        specificRules.some((r) => r.fieldId === f.id && r.hidden === true);
      const hasRemovedBankReference = updatedTemplate.steps.some((stepItem) =>
        stepItem.fields.some((field) => (field.unGroupedFields ?? []).some(isRemovedBankReference))
      );
      if (!hasRemovedBankReference) return updatedTemplate;
      return {
        ...updatedTemplate,
        steps: updatedTemplate.steps
          .map((stepItem) => ({
            ...stepItem,
            fields: stepItem.fields
              .map((field) =>
                field.stepType === 'NOT_GROUPED' && field.unGroupedFields
                  ? {
                      ...field,
                      unGroupedFields: field.unGroupedFields.filter(
                        (f) => !isRemovedBankReference(f)
                      ),
                    }
                  : field
              )
              .filter(
                (field) => field.stepType !== 'NOT_GROUPED' || (field.unGroupedFields ?? []).length
              ),
          }))
          .filter(
            (stepItem, index) =>
              index === 0 ||
              stepItem.fields.length > 0 ||
              !selectedTemplate.steps[index]?.fields.some((field) =>
                (field.unGroupedFields ?? []).some(isRemovedBankReference)
              )
          ),
      };
    });
  }, [
    creditApplication?.groupedSpecialConditions,
    creditApplication?.specificRules,
    selectedTemplate,
  ]);

  useEffect(() => {
    if (
      editableSelectedTemplate &&
      editableSelectedTemplate.steps &&
      editableSelectedTemplate.steps.length > 0
    ) {
      const templateSteps = editableSelectedTemplate.steps.map((st) => st.name);
      templateSteps.push('Review & Submit');
      setSteps(templateSteps);

      setAllStepFieldsValues((currentFields) => {
        const existingFields = currentFields;
        const allStepFields = editableSelectedTemplate.steps.flatMap((st) => {
          return st.fields.flatMap((field) => {
            if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
              return field.unGroupedFields.map((f) => {
                const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                  field.columnType
                } ${0} ${f.id || ''}`
                  .toLowerCase()
                  .replace(/\s+/g, '-');
                let value =
                  f.fromField === 'TEXT' ||
                  f.fromField === 'TEXT_AREA' ||
                  f.fromField === 'OPTION_FIELD' ||
                  f.fromField === 'NUMBER' ||
                  f.fromField === 'CURRENCY'
                    ? ''
                    : f.fromField === 'CHECK_BOX' || f.fromField === 'AUTOFILL_CHECKBOX'
                    ? false
                    : null;
                const foundField = existingFields.find(
                  (stFd) => stFd.id === (f.id || '') && stFd.groupedPosition === 0
                );
                let fieldValue: ConditionalOptionValues[] | null = foundField?.fieldValue ?? null;
                if (foundField) {
                  value = foundField.value;
                }
                if (f.conditionalOptionFields) {
                  const selectedOptionKey = value;
                  const existingConditionalValues = fieldValue ?? [];
                  fieldValue = Object.entries(f.conditionalOptionFields).flatMap(
                    ([key, fieldDef]) => {
                      if (!fieldDef) return [];
                      const optionValues = deriveOptionState({
                        optionKey: key,
                        fieldDef,
                        savedValues: existingConditionalValues,
                        existingValues: existingConditionalValues,
                        fromField: f.fromField,
                        staticPreview: false,
                      }).values;
                      return key === selectedOptionKey
                        ? optionValues
                        : optionValues.map((v) => ({ ...v, hidden: true }));
                    }
                  );
                }
                if (f.fromField === 'DOCUMENT_FIELD') {
                  value = f.documentText || value || '';
                }
                if (f.id === 'email-personal' && value === '') {
                  value = userEmail || '';
                }
                if (f.id === 'first-name-personal' && value === '') {
                  value = userFirstName || '';
                }
                if (f.id === 'last-name-personal' && value === '') {
                  value = userLastName || '';
                }
                if (
                  f.fromField === 'DATE' &&
                  f.defaultValue?.toLowerCase().trim() === 'current' &&
                  !value
                ) {
                  value = new Date().toDateString();
                }

                return {
                  uniqueId: uniqueId,
                  id: f.id || '',
                  name: f.name || '',
                  fromField: f.fromField || '',
                  isRequired: f.isRequired || false,
                  isHashed: f.isHashed || false,
                  stepName: st.name,
                  stepNumber: st.position || 0,
                  groupStepPosition: field.position || 0,
                  groupedPosition: 0,
                  groupName: field?.groupName || '',
                  groupColumnType: field.columnType || ('ONE_COLUMN' as const),
                  isFieldArray: false,
                  isFieldEmail: f.isFieldEmail || false,
                  index: 0,
                  value: value,
                  ...(f.optionType ? { optionType: f.optionType } : {}),
                  ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                  ...(f.status ? { status: f.status } : {}),
                  ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                  ...(field.sendEmail ? { sendEmail: field.sendEmail } : {}),
                  ...(f.type ? { type: f.type } : {}),
                  ...(fieldValue ? { fieldValue } : {}),
                  ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
                  ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
                  ...(f?.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
                  ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
                  ...(f.populateFieldSignatureId
                    ? { populateFieldSignatureId: f.populateFieldSignatureId }
                    : {}),
                  ...(f.autoPopulateFieldIds
                    ? { autoPopulateFieldIds: f.autoPopulateFieldIds }
                    : {}),
                  ...(f.copyFromFieldIds ? { copyFromFieldIds: f.copyFromFieldIds } : {}),
                  ...(f.copyToFieldIds ? { copyToFieldIds: f.copyToFieldIds } : {}),
                  ...(f.conditionalVisibility
                    ? { conditionalVisibility: f.conditionalVisibility }
                    : {}),
                  ...(f.conditionalRequired ? { conditionalRequired: f.conditionalRequired } : {}),
                  ...(f.datePickerFormat ? { datePickerFormat: f.datePickerFormat } : {}),
                  ...(f.validationType ? { validationType: f.validationType } : {}),
                  ...(f.emailValidationType ? { emailValidationType: f.emailValidationType } : {}),
                  ...(f.maxLength !== undefined ? { maxLength: f.maxLength } : {}),
                  ...((f as any).currencyDecimalPlaces !== undefined
                    ? { currencyDecimalPlaces: (f as any).currencyDecimalPlaces }
                    : {}),
                  ...(f.borderId ? { borderId: f.borderId } : {}),
                };
              });
            }

            if (field.stepType === 'GROUPED' && field.groupedFields) {
              const groupFieldEntries = field.groupedFields.flatMap((group) =>
                group.fields.map((f) => {
                  const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                    field.columnType
                  } ${group.groupedPosition} ${f.id || ''}`
                    .toLowerCase()
                    .replace(/\s+/g, '-');
                  let value =
                    f.fromField === 'TEXT' ||
                    f.fromField === 'TEXT_AREA' ||
                    f.fromField === 'OPTION_FIELD' ||
                    f.fromField === 'NUMBER' ||
                    f.fromField === 'CURRENCY'
                      ? ''
                      : f.fromField === 'CHECK_BOX' || f.fromField === 'AUTOFILL_CHECKBOX'
                      ? false
                      : null;
                  const foundField = existingFields.find(
                    (stFd) =>
                      stFd.id === (f.id || '') &&
                      stFd.groupedPosition === (group.groupedPosition || 0)
                  );
                  let fieldValue: ConditionalOptionValues[] | null = foundField?.fieldValue ?? null;
                  if (foundField) {
                    value = foundField.value;
                  }
                  if (f.conditionalOptionFields) {
                    const selectedOptionKey = value;
                    const existingConditionalValues = fieldValue ?? [];
                    fieldValue = Object.entries(f.conditionalOptionFields).flatMap(
                      ([key, fieldDef]) => {
                        if (!fieldDef) return [];
                        const optionValues = deriveOptionState({
                          optionKey: key,
                          fieldDef,
                          savedValues: existingConditionalValues,
                          existingValues: existingConditionalValues,
                          fromField: f.fromField,
                          staticPreview: false,
                        }).values;
                        return key === selectedOptionKey
                          ? optionValues
                          : optionValues.map((v) => ({ ...v, hidden: true }));
                      }
                    );
                  }
                  if (f.fromField === 'DOCUMENT_FIELD') {
                    value = f.documentText || value || '';
                  }
                  if (f.id === 'email-personal' && value === '') {
                    value = userEmail || '';
                  }
                  if (f.id === 'first-name-personal' && value === '') {
                    value = userFirstName || '';
                  }
                  if (f.id === 'last-name-personal' && value === '') {
                    value = userLastName || '';
                  }

                  if (
                    f.fromField === 'DATE' &&
                    f.defaultValue?.toLowerCase().trim() === 'current' &&
                    !value
                  ) {
                    value = new Date().toDateString();
                  }
                  return {
                    uniqueId: uniqueId,
                    id: f.id || '',
                    name: f.name || '',
                    fromField: f.fromField || '',
                    isRequired: f.isRequired || false,
                    isHashed: f.isHashed || false,
                    stepName: st.name,
                    stepNumber: st.position || 0,
                    groupStepPosition: field.position || 0,
                    groupedPosition: group.groupedPosition || 0,
                    groupName: field?.groupName || '',
                    groupColumnType: group.columnType || ('ONE_COLUMN' as const),
                    isFieldArray: field.isFieldArray || false,
                    isFieldEmail: f.isFieldEmail || false,
                    index: group.index || 0,
                    value: value,
                    ...(f.optionType ? { optionType: f.optionType } : {}),
                    ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                    ...(f.status ? { status: f.status } : {}),
                    ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                    ...(field.sendEmail ? { sendEmail: field.sendEmail } : {}),
                    ...(f.type ? { type: f.type } : {}),
                    ...(fieldValue ? { fieldValue } : {}),
                    ...(f.onlyShowPlaidOption
                      ? { onlyShowPlaidOption: f.onlyShowPlaidOption }
                      : {}),
                    ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
                    ...(f?.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
                    ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
                    ...(f.populateFieldSignatureId
                      ? { populateFieldSignatureId: f.populateFieldSignatureId }
                      : {}),
                    ...(f.autoPopulateFieldIds
                      ? { autoPopulateFieldIds: f.autoPopulateFieldIds }
                      : {}),
                    ...(f.copyFromFieldIds ? { copyFromFieldIds: f.copyFromFieldIds } : {}),
                    ...(f.copyToFieldIds ? { copyToFieldIds: f.copyToFieldIds } : {}),
                    ...(f.conditionalVisibility
                      ? { conditionalVisibility: f.conditionalVisibility }
                      : {}),
                    ...(f.conditionalRequired
                      ? { conditionalRequired: f.conditionalRequired }
                      : {}),
                    ...(f.datePickerFormat ? { datePickerFormat: f.datePickerFormat } : {}),
                    ...(f.validationType ? { validationType: f.validationType } : {}),
                    ...(f.emailValidationType
                      ? { emailValidationType: f.emailValidationType }
                      : {}),
                    ...(f.maxLength !== undefined ? { maxLength: f.maxLength } : {}),
                    ...((f as any).currencyDecimalPlaces !== undefined
                      ? { currencyDecimalPlaces: (f as any).currencyDecimalPlaces }
                      : {}),
                    ...(f.borderId ? { borderId: f.borderId } : {}),
                  };
                })
              );

              if (field.hasTradeReferenceUpload) {
                const ocrUploadId = `${field.groupName}-ocr-upload`
                  .toLowerCase()
                  .replace(/\s+/g, '-');
                const ocrUniqueId =
                  `${st.name} ${field?.groupName} ${field.position} ${field.columnType} 0 ocr-upload`
                    .toLowerCase()
                    .replace(/\s+/g, '-');
                const foundUploadField = existingFields.find(
                  (stFd) =>
                    stFd.id === ocrUploadId && stFd.groupStepPosition === (field.position || 0)
                );
                groupFieldEntries.push({
                  uniqueId: ocrUniqueId,
                  id: ocrUploadId,
                  name: `${field?.groupName ?? ''} AI Upload`,
                  fromField: 'TRADE_REFERENCE',
                  isRequired: false,
                  isHashed: false,
                  stepName: st.name,
                  stepNumber: st.position || 0,
                  groupStepPosition: field.position || 0,
                  groupedPosition: 0,
                  groupName: field?.groupName || '',
                  groupColumnType: 'ONE_COLUMN' as const,
                  isFieldArray: field.isFieldArray || false,
                  isFieldEmail: false,
                  index: 0,
                  value: foundUploadField?.value ?? null,
                });
              }

              return groupFieldEntries;
            }

            return [];
          });
        });
        return allStepFields;
      });
      return;
    }

    setSteps([]);
    setAllStepFieldsValues([]);
  }, [editableSelectedTemplate, userEmail, userFirstName, userLastName]);

  // Mirrors the "checked" logic DynamicStepForm uses to render an AUTOFILL_CHECKBOX:
  // checked either because its stored value is true, or because the source/target
  // fields already happen to hold equal values (e.g. pre-populated from an existing
  // application, never explicitly toggled).
  const isAutofillCheckboxEffectivelyChecked = (
    checkboxField: (typeof allStepFieldsValues)[number],
    fields: typeof allStepFieldsValues
  ) => {
    if (checkboxField.value === true) return true;
    const sourceIds = (checkboxField.copyFromFieldIds ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const targetIds = (checkboxField.copyToFieldIds ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (targetIds.length === 0) return false;
    const allMatch = targetIds.every((targetId, i) => {
      const sourceId = sourceIds[i] ?? sourceIds[0];
      const sourceValue = fields.find((f) => f.id === sourceId)?.value ?? '';
      const targetValue = fields.find((f) => f.id === targetId)?.value ?? '';
      return sourceValue === targetValue;
    });
    const hasSourceValue = sourceIds.some((sourceId) => {
      const sourceValue = fields.find((f) => f.id === sourceId)?.value;
      return sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
    });
    return allMatch && hasSourceValue;
  };

  const onChangeFormData = ({
    id,
    value,
    secondaryValue,
    fieldValue,
    addressVerification,
  }: {
    id: string;
    value: any;
    secondaryValue?: any;
    fieldValue?: ConditionalOptionValues[];
    addressVerification?: AddressVerification | null;
  }) => {
    const currentField = allStepFieldsValues.find((f) => f.uniqueId === id);

    if (!currentField) return;

    const fieldUniqueId = currentField.id;

    let clearedErrorUniqueIds = [id];
    if (currentField.fromField === 'AUTOFILL_CHECKBOX') {
      const targetIds = (currentField.copyToFieldIds ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      clearedErrorUniqueIds = [
        id,
        ...allStepFieldsValues.filter((f) => targetIds.includes(f.id)).map((f) => f.uniqueId),
      ];
    } else {
      // If this field is the copy source for an already-checked AUTOFILL_CHECKBOX,
      // its target(s) are about to be resynced below — clear their errors too.
      allStepFieldsValues.forEach((f) => {
        if (
          f.fromField !== 'AUTOFILL_CHECKBOX' ||
          !isAutofillCheckboxEffectivelyChecked(f, allStepFieldsValues)
        )
          return;
        const sourceIds = (f.copyFromFieldIds ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const targetIds = (f.copyToFieldIds ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        targetIds.forEach((targetId, i) => {
          const sourceId = sourceIds[i] ?? sourceIds[0];
          if (sourceId !== fieldUniqueId) return;
          const targetField = allStepFieldsValues.find((tf) => tf.id === targetId);
          if (targetField) clearedErrorUniqueIds.push(targetField.uniqueId);
        });
      });
    }

    setAllStepFieldsValues((prev) => {
      const autofillValueMap: Record<string, any> = {};
      if (currentField.fromField === 'AUTOFILL_CHECKBOX') {
        const sourceIds = (currentField.copyFromFieldIds ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const targetIds = (currentField.copyToFieldIds ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (value === true) {
          targetIds.forEach((targetId, i) => {
            const sourceId = sourceIds[i] ?? sourceIds[0];
            if (sourceId) {
              autofillValueMap[targetId] = prev.find((f) => f.id === sourceId)?.value;
            }
          });
        } else {
          targetIds.forEach((targetId) => {
            const targetField = prev.find((f) => f.id === targetId);
            if (!targetField) return;
            autofillValueMap[targetId] =
              targetField.fromField === 'CHECK_BOX' || targetField.fromField === 'AUTOFILL_CHECKBOX'
                ? false
                : targetField.fromField === 'TEXT' ||
                  targetField.fromField === 'TEXT_AREA' ||
                  targetField.fromField === 'OPTION_FIELD' ||
                  targetField.fromField === 'NUMBER' ||
                  targetField.fromField === 'CURRENCY'
                ? ''
                : null;
          });
        }
      } else {
        // The field being changed isn't the checkbox itself — if it's the copy source
        // for a checkbox that's already checked, keep the target(s) in sync with it
        // (e.g. Company Name changes after autofill while "same as DBA" is checked).
        prev.forEach((f) => {
          if (f.fromField !== 'AUTOFILL_CHECKBOX' || !isAutofillCheckboxEffectivelyChecked(f, prev))
            return;
          const sourceIds = (f.copyFromFieldIds ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          const targetIds = (f.copyToFieldIds ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          targetIds.forEach((targetId, i) => {
            const sourceId = sourceIds[i] ?? sourceIds[0];
            if (sourceId === fieldUniqueId) {
              autofillValueMap[targetId] = value;
            }
          });
        });
      }

      // If this edit lands directly on the *target* field of a checked
      // AUTOFILL_CHECKBOX (e.g. typing straight into DBA), and it no longer
      // matches its source, uncheck that checkbox so its state reflects reality
      // instead of staying stuck checked.
      const checkboxValueMap: Record<string, boolean> = {};
      if (currentField.fromField !== 'AUTOFILL_CHECKBOX') {
        prev.forEach((f) => {
          if (f.fromField !== 'AUTOFILL_CHECKBOX' || !isAutofillCheckboxEffectivelyChecked(f, prev))
            return;
          const sourceIds = (f.copyFromFieldIds ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          const targetIds = (f.copyToFieldIds ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          const targetIndex = targetIds.indexOf(fieldUniqueId);
          if (targetIndex === -1) return;
          const sourceId = sourceIds[targetIndex] ?? sourceIds[0];
          const sourceValue = prev.find((sf) => sf.id === sourceId)?.value ?? '';
          if (value !== sourceValue) {
            checkboxValueMap[f.id] = false;
          }
        });
      }

      const updated = prev.map((f) => {
        if (f.uniqueId === id) {
          const updatedField = {
            ...f,
            value,
            ...(secondaryValue !== undefined ? { secondaryValue } : {}),
            ...(fieldValue !== undefined ? { fieldValue } : {}),
            ...(addressVerification !== undefined ? { addressVerification } : {}),
          };
          return updatedField;
        }

        if (f.id in checkboxValueMap) {
          return { ...f, value: checkboxValueMap[f.id] };
        }

        if (f.id in autofillValueMap) {
          return { ...f, value: autofillValueMap[f.id] };
        }

        if (f.linkedFieldId?.includes(fieldUniqueId) && f.fromField !== 'DYNAMIC_FORM_DOCUMENT') {
          const updatedLinked = {
            ...f,
            value,
          };
          return updatedLinked;
        }

        if (f.fromField === 'DYNAMIC_FORM_DOCUMENT') {
          const fieldVal = (f.fieldValue ?? []).map((fv) => {
            if (fv.linkedFieldId?.includes(fieldUniqueId)) {
              const updatedNested = {
                ...fv,
                value,
              };
              return updatedNested;
            }

            return fv;
          });

          const updatedDocField = {
            ...f,
            fieldValue: fieldVal,
          };

          return updatedDocField;
        }

        return f;
      });

      return updated;
    });

    setErrors((prev) => prev.filter((f) => !clearedErrorUniqueIds.includes(f.uniqueId)));
    setHasConditionalFieldErrors(false);

    const foundInvitedSig = originalInvitedSignatures.find(
      (invSig) => invSig?.linkedFieldId && id.includes(invSig.linkedFieldId)
    );

    if (!foundInvitedSig) return;

    setInvitedSignatures((prev) => {
      const alreadyExists = prev.some((sig) => sig.placeHolder === foundInvitedSig.placeHolder);

      if (secondaryValue === 'Yes' && value === null) {
        if (!alreadyExists) {
          return [...prev, foundInvitedSig];
        }
        return prev;
      }

      return prev.filter((sig) => sig.placeHolder !== foundInvitedSig.placeHolder);
    });
  };

  useEffect(() => {
    if (
      !creditApplication ||
      !creditApplicationId ||
      !['SENT', 'OPENED', 'DRAFT'].includes(creditApplication.status)
    ) {
      return;
    }

    ['email-personal', 'first-name-personal', 'last-name-personal'].forEach((idPart) => {
      const foundField = creditApplication.fieldValues.find((f) => f.id.includes(idPart));
      const currentFieldValue = allStepFieldsValues.find(
        (f) => f.id.includes(idPart) && f.value === ''
      );
      if (foundField && currentFieldValue) {
        onChangeFormData({ id: foundField.uniqueId, value: foundField.value });
      }
    });
  }, [creditApplication, creditApplicationId]);

  useEffect(() => {
    if (!draftCreditApplication) {
      return;
    }

    const run = async () => {
      creditApplicationIdRef.current = draftCreditApplication.id;

      const { decryptedFields, decryptedBankReferences, decryptedInvitedSignatures } =
        await decryptCreditApplicationData(draftCreditApplication, decryptValue);

      // ---- Update form values ----
      setAllStepFieldsValues((prev) =>
        prev.map((f) => {
          const match = decryptedFields.find(
            (fld) => fld.id === f.id && fld.groupedPosition === f.groupedPosition
          );
          if (!match) return f;

          if (f.fromField === 'CONDITIONAL_SELECTED_FIELD') {
            const isLocallyEmpty = f.value === '' || f.value === null || f.value === undefined;
            if (!isLocallyEmpty) return f;
            return {
              ...f,
              ...match,
              uniqueId: f.uniqueId,
              groupStepPosition: f.groupStepPosition,
              groupedPosition: f.groupedPosition,
              groupName: f.groupName,
              stepName: f.stepName,
              stepNumber: f.stepNumber,
            };
          }

          return {
            ...f,
            value: f.fromField === 'DOCUMENT_FIELD' ? match.value || f.value : match.value,
            ...(match.secondaryValue !== undefined && {
              secondaryValue: match.secondaryValue,
            }),
            ...(match.fieldValue !== undefined && {
              fieldValue: match.fieldValue,
            }),
          };
        })
      );

      // ---- Set bank references ----
      if (decryptedBankReferences.length > 0) {
        setBankReference(decryptedBankReferences);
      }

      // ---- Invited signatures ----
      if (decryptedInvitedSignatures?.length) {
        setInvitedSignatures((prev) =>
          prev.map((f) => {
            const match = decryptedInvitedSignatures.find(
              (invite) => invite.placeHolder === f.placeHolder && invite.type === f.type
            );
            return match ? { ...f, ...match } : f;
          })
        );
      }
    };

    run();
  }, [draftCreditApplication, selectedTemplate, decryptValue]);

  useEffect(() => {
    if (!prefillDraftApplication) return;

    const run = async () => {
      const { decryptedFields, decryptedBankReferences, decryptedInvitedSignatures } =
        await decryptCreditApplicationData(prefillDraftApplication, decryptValue);

      setAllStepFieldsValues((prev) =>
        prev.map((f) => {
          const match = decryptedFields.find(
            (fld) => fld.id === f.id && fld.groupedPosition === f.groupedPosition
          );
          if (!match) return f;

          if (f.fromField === 'CONDITIONAL_SELECTED_FIELD') {
            const isLocallyEmpty = f.value === '' || f.value === null || f.value === undefined;
            if (!isLocallyEmpty) return f;
            return {
              ...f,
              ...match,
              uniqueId: f.uniqueId,
              groupStepPosition: f.groupStepPosition,
              groupedPosition: f.groupedPosition,
              groupName: f.groupName,
              stepName: f.stepName,
              stepNumber: f.stepNumber,
            };
          }

          return {
            ...f,
            value: f.fromField === 'DOCUMENT_FIELD' ? match.value || f.value : match.value,
            ...(match.secondaryValue !== undefined && {
              secondaryValue: match.secondaryValue,
            }),
            ...(match.fieldValue !== undefined && {
              fieldValue: match.fieldValue,
            }),
          };
        })
      );

      if (decryptedBankReferences.length > 0) {
        setBankReference(decryptedBankReferences);
      }

      if (decryptedInvitedSignatures?.length) {
        setInvitedSignatures((prev) =>
          prev.map((f) => {
            const match = decryptedInvitedSignatures.find(
              (invite) => invite.placeHolder === f.placeHolder && invite.type === f.type
            );
            return match ? { ...f, ...match } : f;
          })
        );
      }
    };

    run();
  }, [prefillDraftApplication, selectedTemplate, decryptValue]);

  useEffect(() => {
    if (!creditApplication || !creditApplicationId || !senderEmail) return;

    const applyDecryptedValues = async () => {
      const { decryptedFields, decryptedBankReferences, decryptedInvitedSignatures } =
        await decryptCreditApplicationData(creditApplication, decryptValue);

      setAllStepFieldsValues((prev) =>
        prev.map((f) => {
          const fieldValue = decryptedFields.find(
            (fv) => fv.id === f.id && fv.groupedPosition === f.groupedPosition
          );

          if (!fieldValue) return f;

          // A background sync (e.g. the SENT->OPENED status update, or a draft-save echo) can
          // resolve after the applicant has already made a selection here, carrying a snapshot
          // of `creditApplication` from before that selection. Only fill these in when the local
          // field is still empty — otherwise this silently reverts a just-picked conditional
          // option (or filled-in document field) back to blank. Mirrors the guard below that
          // already protects every other field type.
          const isLocallyEmpty = f.value === '' || f.value === null || f.value === undefined;

          if (f.fromField === 'CONDITIONAL_SELECTED_FIELD') {
            if (!isLocallyEmpty) return f;
            return {
              ...f,
              ...fieldValue,
              uniqueId: f.uniqueId,
              isRequired: f.isRequired,
              groupStepPosition: f.groupStepPosition,
              groupedPosition: f.groupedPosition,
              groupName: f.groupName,
              stepName: f.stepName,
              stepNumber: f.stepNumber,
            };
          }

          if (f.fromField === 'DYNAMIC_FORM_DOCUMENT') {
            if (!isLocallyEmpty) return f;
            return {
              ...f,
              ...fieldValue,
              isRequired: f.isRequired,
              groupStepPosition: f.groupStepPosition,
              groupedPosition: f.groupedPosition,
              groupName: f.groupName,
              stepName: f.stepName,
              stepNumber: f.stepNumber,
            };
          }

          if (f.value === '' || f.value === null || f.value === 0) {
            return {
              ...f,
              ...fieldValue,
              isRequired: f.isRequired,
              groupStepPosition: f.groupStepPosition,
              groupedPosition: f.groupedPosition,
              groupName: f.groupName,
              stepName: f.stepName,
              stepNumber: f.stepNumber,
            };
          }

          return f;
        })
      );

      // The invite may have been forwarded: the person filling it in verified a different email
      // than the one the supplier sent it to. Keep their contact details instead of reverting to
      // the invited contact's that were prefilled on the sent application.
      const isForwardedApplicant =
        !!userEmail &&
        userEmail.trim().toLowerCase() !==
          (creditApplication.senderEmail ?? '').trim().toLowerCase();
      if (isForwardedApplicant) {
        const applicantOverrides: Record<string, string> = {
          'email-personal': userEmail,
          ...(userFirstName ? { 'first-name-personal': userFirstName } : {}),
          ...(userLastName ? { 'last-name-personal': userLastName } : {}),
        };
        setAllStepFieldsValues((prev) =>
          prev.map((f) =>
            applicantOverrides[f.id] !== undefined ? { ...f, value: applicantOverrides[f.id] } : f
          )
        );
      }

      const emailField = creditApplication.fieldValues.find((f) => f.id === 'email-personal');
      const emptyEmailField = allStepFieldsValues.find(
        (f) => f.id === 'email-personal' && f.value === ''
      );
      if (emailField && emptyEmailField && !isForwardedApplicant) {
        onChangeFormData({ id: emailField.uniqueId, value: senderEmail });
      }

      if (decryptedBankReferences.length > 0) {
        setBankReference(decryptedBankReferences);
      }

      if (decryptedInvitedSignatures) {
        setInvitedSignatures((prev) =>
          prev.map((f) => {
            const match = decryptedInvitedSignatures.find(
              (invite) => invite.placeHolder === f.placeHolder && invite.type === f.type
            );
            return match ? { ...f, ...match } : f;
          })
        );
      }
    };

    applyDecryptedValues().catch((error) => {
      console.error('Failed to decrypt credit application fields:', error);
    });
  }, [creditApplication, creditApplicationId, senderEmail, editableSelectedTemplate, decryptValue]);

  const validateForm = () => {
    const currentStepFields = allStepFieldsValues.filter(
      (f) => f.stepName === steps[step] && f.fromField !== 'BANK_REFERENCE'
    );

    const fieldErrors: { uniqueId: string; errorText: string }[] = [];

    for (const field of currentStepFields) {
      if (
        !isFieldShownForAnswers(field, (fieldId) =>
          allStepFieldsValues.find((f) => f.id === fieldId)
        )
      ) {
        continue;
      }

      // Required field check — conditionalRequired fields only while their condition is met. An
      // invite (e.g. Personal Guarantor) or its form document has no value of its own: the
      // invite's email is checked by validateInviteSigners instead.
      const isRequired =
        isFieldRequiredForAnswers(
          field,
          (id) => allStepFieldsValues.find((f) => f.id === id)?.value
        ) &&
        !(
          !!field.conditionalRequired?.sourceFieldId &&
          (field.fromField === 'INVITE_USER' || field.fromField === 'DYNAMIC_FORM_DOCUMENT')
        );
      let value = field.value;
      if (typeof value === 'string') {
        value = value.trim();
      }
      if (
        isRequired &&
        (value === '' || value === null) &&
        field.fromField !== 'CONDITIONAL_FORM' &&
        field.fromField !== 'CONDITIONAL_SELECTED_FIELD'
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} is required.`,
        });
      }

      if (isRequired && field.fromField === 'CONDITIONAL_FORM') {
        if (value === null && !field.secondaryValue) {
          fieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: `${field.name} is required.`,
          });
        }
      }

      if (isRequired && field.fromField === 'CONDITIONAL_SELECTED_FIELD') {
        const hasValue = value !== null && value !== '';
        const hasFieldValue =
          field.fieldValue &&
          field.fieldValue.some(
            (fv) => fv.value !== null && fv.value !== '' && fv.value !== undefined
          );
        if (!hasValue && !hasFieldValue) {
          fieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: isHTML(field.name) ? 'Field is required.' : `${field.name} is required.`,
          });
        }
      }

      // Email format validation
      else if (field.fromField === 'TEXT' && field.isFieldEmail && field.value) {
        const singleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if ((field.emailValidationType ?? 'SINGLE') === 'MULTIPLE') {
          const emails = field.value
            .toString()
            .split(/[,;]/)
            .map((e: string) => e.trim())
            .filter(Boolean);
          const invalidEmail = emails.find((e: string) => !singleEmailRegex.test(e));
          if (invalidEmail) {
            fieldErrors.push({
              uniqueId: field.uniqueId,
              errorText: `"${invalidEmail}" is not a valid email address.`,
            });
          }
        } else if (!singleEmailRegex.test(field.value.toString())) {
          fieldErrors.push({
            uniqueId: field.uniqueId,
            errorText: `${field.name} must be a valid email.`,
          });
        }
      }

      // Tax ID / EIN format validation (XX-XXXXXXX)
      else if (
        field.fromField === 'TEXT' &&
        field.validationType === 'EIN' &&
        field.value &&
        !/^\d{2}-\d{7}$/.test(field.value.toString().trim())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be in XX-XXXXXXX format (e.g. 12-3456789).`,
        });
      }

      // SSN format validation (XXX-XX-XXXX)
      else if (
        field.fromField === 'TEXT' &&
        field.validationType === 'SSN' &&
        field.value &&
        !/^\d{3}-\d{2}-\d{4}$/.test(field.value.toString().trim())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be in XXX-XX-XXXX format (e.g. 123-45-6789).`,
        });
      }

      // Phone number format validation (XXX-XXX-XXXX)
      else if (
        field.fromField === 'TEXT' &&
        field.validationType === 'PHONE' &&
        field.value &&
        !/^\d{3}-\d{3}-\d{4}$/.test(field.value.toString().trim())
      ) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be in XXX-XXX-XXXX format (e.g. 555-123-4567).`,
        });
      }
    }

    // Trade reference email validations
    const personalEmail = allStepFieldsValues
      .find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase();

    const tradeRefEmailFields = currentStepFields.filter(
      (f) => f.id.includes('trade-reference-company-email') && f.fromField === 'TEXT' && f.value
    );

    // Neither the supplier's own staff nor the applicant's own coworkers can vouch for the
    // applicant — see getBlockedTradeReferenceEmailDomains.
    const blockedTradeRefDomains = getBlockedTradeReferenceEmailDomains(basicSupplier, [
      userEmail,
      senderEmail,
      personalEmail,
    ]);

    const seenTradeEmails: string[] = [];
    for (const field of tradeRefEmailFields) {
      const emailVal = field.value.toString().trim().toLowerCase();

      if (findBlockedTradeReferenceEmailDomain(emailVal, blockedTradeRefDomains)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: blockedTradeReferenceEmailMessage(basicSupplier?.name),
        });
        continue;
      }

      if (personalEmail && emailVal === personalEmail) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText: 'Trade reference email cannot be the same as your personal email.',
        });
        continue;
      }

      if (seenTradeEmails.includes(emailVal)) {
        fieldErrors.push({
          uniqueId: field.uniqueId,
          errorText:
            'Duplicate trade reference email. Each trade reference must have a unique email.',
        });
      } else {
        seenTradeEmails.push(emailVal);
      }
    }

    setErrors(fieldErrors);
    return fieldErrors.length === 0;
  };

  // An invite (e.g. Personal Guarantor) whose field is required only under a condition that isn't
  // met: it can be left blank, and a blank one isn't sent.
  const isOptionalInvite = (sig: StateInvitedUser) => {
    const inviteField = sig.id ? allStepFieldsValues.find((f) => f.id === sig.id) : undefined;
    return (
      !!inviteField?.conditionalRequired?.sourceFieldId &&
      !isFieldRequiredForAnswers(
        inviteField,
        (id) => allStepFieldsValues.find((f) => f.id === id)?.value
      )
    );
  };

  const validateInviteSigners = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let foundInvitedSig = invitedSignatures.filter((sig) => {
      if (!sig.id) return true;
      const inviteField = allStepFieldsValues.find((f) => f.id === sig.id);
      if (!inviteField?.conditionalVisibility?.sourceFieldId) return true;
      const { sourceFieldId, sourceOptionValues } = inviteField.conditionalVisibility;
      const sourceValue = String(
        allStepFieldsValues.find((f) => f.id === sourceFieldId)?.value ?? ''
      );
      return sourceOptionValues.includes(sourceValue);
    });

    const bankRefField = allStepFieldsValues.find((f) => f.fromField === 'BANK_REFERENCE');
    if (
      bankRefField &&
      bankRefField.showIfNotPlaid &&
      bankRefField.linkedFieldId &&
      bankReference?.length
    ) {
      const manualBankRef = bankReference.find((f) => f.connectionType === 'manual');

      if (!manualBankRef) {
        const linkedId = bankRefField.linkedFieldId;

        const sig = invitedSignatures.filter((s) => s?.id && !linkedId.includes(s.id));

        foundInvitedSig = sig;
      }
    }

    const foundMinimumCreditAmount =
      foundInvitedSig.find(
        (item) =>
          (item.minimumCreditAmount && item.minimumCreditAmount > 0 && item.type === 'GUARANTOR') ||
          item.type === 'CORPORATE_GUARANTOR'
      )?.minimumCreditAmount ?? 0;

    const foundAmount = allStepFieldsValues.find((f) => f.id.includes('amount-of'))?.value ?? 0;

    const validateEmail = (email: string, placeHolder: string, customMessage?: string) => {
      if (!email || email.trim() === '') {
        return customMessage || `${placeHolder} email is required.`;
      }
      if (!emailRegex.test(email)) {
        return `${placeHolder} email is not valid.`;
      }
      return '';
    };

    const validateByType = (item: StateInvitedUser) => {
      if (isOptionalInvite(item)) {
        return item.email?.trim() && !emailRegex.test(item.email)
          ? `${item?.placeHolder ?? ''} email is not valid.`
          : '';
      }
      const signers = foundInvitedSig.filter((s) => s.type === item.type && s.email !== '');
      const invited = foundInvitedSig.filter((s) => s.type === item.type);

      const min = item.minimumNumberOfSignatures;
      const hasMinDefined = min !== undefined;

      const requiresAmountCheck =
        item.type === 'GUARANTOR' ||
        (item.type === 'CORPORATE_GUARANTOR' && foundAmount > foundMinimumCreditAmount);

      let errorText = '';

      if (hasMinDefined && min > signers.length && min !== invited.length) {
        errorText = validateEmail(
          item.email,
          item?.placeHolder ?? '',
          `At least ${min} ${item.type.toLowerCase().replace('_', ' ')} email is required.`
        );
      } else if (hasMinDefined && min > signers.length && min === invited.length) {
        errorText = validateEmail(item.email, item?.placeHolder ?? '');
      } else if (!hasMinDefined && requiresAmountCheck) {
        errorText = validateEmail(item.email, item?.placeHolder ?? '');
      } else if (item.email?.trim() !== '' && !emailRegex.test(item.email)) {
        errorText = `${item?.placeHolder ?? ''} email is not valid.`;
      }

      return errorText;
    };

    /* -------------------------
     BASE VALIDATION
  -------------------------*/
    const inviteErrors = foundInvitedSig.map((item) => {
      let errorText = '';

      if (item.step === step) {
        errorText = validateByType(item);
      }

      return {
        placeHolder: item?.placeHolder ?? '',
        errorText,
      };
    });

    /* -------------------------
     UNIQUE EMAIL VALIDATION
     (ONLY CURRENT STEP)
  -------------------------*/
    const typeMap: Record<string, Map<string, StateInvitedUser[]>> = {};
    const uniqueErrors: { placeHolder: string; errorText: string }[] = [];

    foundInvitedSig
      .filter((item) => item.step === step && item.email?.trim() !== '')
      .forEach((item) => {
        if (!typeMap[item.type]) {
          typeMap[item.type] = new Map();
        }

        const emailMap = typeMap[item.type];

        if (!emailMap.has(item.email.toLowerCase())) {
          emailMap.set(item.email.toLowerCase(), []);
        }

        emailMap.get(item.email.toLowerCase())!.push(item);
      });

    Object.values(typeMap).forEach((emailMap) => {
      emailMap.forEach((items) => {
        if (items.length > 1) {
          items.forEach((item) => {
            uniqueErrors.push({
              placeHolder: item.placeHolder ?? '',
              errorText: 'Email should not be the same.',
            });
          });
        }
      });
    });

    /* -------------------------
     MERGE ERRORS
  -------------------------*/
    const mergedErrors = inviteErrors.map((err) => {
      const uniqueError = uniqueErrors.find((u) => u.placeHolder === err.placeHolder);

      return uniqueError && !err.errorText ? { ...err, errorText: uniqueError.errorText } : err;
    });

    setInvitedSignaturesErrors(mergedErrors);

    return mergedErrors.every((error) => error.errorText === '');
  };

  // Same rule as trade reference emails: a manual bank reference's contact can't be at the
  // supplier or at the applicant's own company — see getBlockedTradeReferenceEmailDomains.
  const blockedBankReferenceEmailDomains = getBlockedTradeReferenceEmailDomains(basicSupplier, [
    userEmail,
    senderEmail,
    allStepFieldsValues
      .find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase(),
  ]);

  const validateBankReferences = () => {
    const currentStepFields = allStepFieldsValues.filter((f) => f.stepName === steps[step]);
    const bankReferenceField = currentStepFields.find(
      (f) => f.fromField === 'BANK_REFERENCE' && f.isRequired
    );
    if (
      currentStepFields.some((f) => f.fromField === 'BANK_REFERENCE') &&
      findBlockedBankReferenceEmail(bankReference, blockedBankReferenceEmailDomains)
    ) {
      setBankError(blockedBankReferenceEmailMessage(basicSupplier?.name));
      return false;
    }
    if (bankReferenceField) {
      if (!bankReference) {
        setBankError('Bank reference is required.');
        return false;
      }
      setBankError(null);
      return true;
    }
    setBankError(null);
    return true;
  };

  const processDynamicFields = async (
    fields: DynamicInvitedSignaturesFields[],
    supplierId: string
  ) => {
    try {
      const processedFields = await Promise.all(
        fields.map(async (field) => {
          let value = field.value;

          // ---- FILE UPLOAD HANDLING ----
          if (value instanceof File) {
            const fileType = value.name.split('.').pop() || '';
            const storagePath = `creditApplicationFiles/${supplierId}/${Date.now()}`;

            try {
              const uploadUrl = await firebaseLargeFileUploadHelper({
                blobUrl: URL.createObjectURL(value),
                fileNameWithExtension: value.name,
                storagePath,
              });

              if (!uploadUrl) throw new Error(`Upload failed for file "${value.name}".`);

              value = { name: value.name, url: uploadUrl, fileType };
            } catch (error) {
              console.error(`❌ File upload error for "${value.name}":`, error);
              throw new Error(`Upload failed for file "${value.name}".`);
            }
          }

          // ---- HASHING ----
          if (field.isHashed) {
            const secretKey = process.env.REACT_APP_CRYPTO_KEY;
            if (secretKey) {
              value = CryptoJS.AES.encrypt(JSON.stringify(value), secretKey).toString();
            }
          }

          return { ...field, value };
        })
      );

      const title = processedFields.find((field) => field.id.includes('title'))?.value || '';

      const signatureInfo = {
        signature,
        dateSigned: new Date().toISOString(),
        title,
        ...(ipdData && signature
          ? { ipAddress: ipdData.ipAddress, location: ipdData.location }
          : {}),
      };

      const baseInvitedSignatures = invitedSignatures.map((sig) => ({
        title: sig.title,
        email: sig.email,
        fields: sig.fields ?? [],
        signature: sig.signature ?? '',
        dateSigned: sig.dateSigned,
        ipAddress: sig.ipAddress,
        location: sig.location,
        placeHolder: sig.placeHolder ?? '',
        type: sig.type ?? ('AUTHORIZED_AGENT' as const),
        ...(sig.minimumCreditAmount && { minimumCreditAmount: sig.minimumCreditAmount }),
        ...(sig.minimumNumberOfSignatures !== undefined && {
          minimumNumberOfSignatures: sig.minimumNumberOfSignatures,
        }),
        ...(sig?.linkedFieldId && { linkedFieldId: sig.linkedFieldId }),
        ...(sig.signaturePageFieldIds && { signaturePageFieldIds: sig.signaturePageFieldIds }),
      }));

      const email = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value ?? '';

      const updatedSignatures = baseInvitedSignatures.map((sig) => {
        if (signedNowPlaceholders.includes(sig.placeHolder)) return sig;
        if (sig.email.trim().toLowerCase() !== email.trim().toLowerCase()) return sig;

        const mergedFields = sig.fields.map((field) => {
          const match = processedFields.find((pf) => field.id.includes(pf.id));
          return match ? { ...field, id: match.id, value: match.value } : field;
        });

        return {
          ...sig,
          ...signatureInfo,
          fields: mergedFields,
        };
      });

      return updatedSignatures;
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
      return null;
    }
  };

  const signatureDetails = useMemo(() => {
    const email =
      (allStepFieldsValues.find((f) => f.id === 'email-personal')?.value as string) ?? '';
    const updatedSignature = invitedSignatures.map((item) => ({
      title: item.title,
      email: item.email,
      fields: item.fields || [],
      signature: '',
      placeHolder: item.placeHolder || '',
      type: item.type || ('AUTHORIZED_AGENT' as const),
      ...(item.minimumCreditAmount ? { minimumCreditAmount: item.minimumCreditAmount } : {}),
      ...(item.minimumNumberOfSignatures !== undefined
        ? {
            minimumNumberOfSignatures: item.minimumNumberOfSignatures,
          }
        : {}),
      ...(item?.linkedFieldId ? { linkedFieldId: item.linkedFieldId } : {}),
      ...(item.signaturePageFieldIds ? { signaturePageFieldIds: item.signaturePageFieldIds } : {}),
    }));

    return { email, invitedSignatures: updatedSignature };
  }, [allStepFieldsValues, invitedSignatures]);

  const signNowInvitedSignatures = useMemo(() => {
    if (!signNowFieldData) return [];
    return [
      {
        placeHolder: signNowFieldData.placeHolder ?? '',
        email: signatureDetails.email,
        fields: signNowFieldData.fields,
        signature: '',
        type: signNowFieldData.type,
      },
    ];
  }, [signNowFieldData, signatureDetails.email]);

  const processData = async (dynamicFieldValues?: DynamicInvitedSignaturesFields[]) => {
    try {
      const secretKey = process.env.REACT_APP_CRYPTO_KEY;
      const fieldsToSubmit = updatedFieldsRef.current || allStepFieldsValues;

      const isConditionallyVisible = (f: AllStepFieldValue) => {
        if (!f.conditionalVisibility?.sourceFieldId) return true;
        const { sourceFieldId, sourceOptionValues } = f.conditionalVisibility;
        const sourceValue = String(
          fieldsToSubmit.find((sf) => sf.id === sourceFieldId)?.value ?? ''
        );
        return sourceOptionValues.includes(sourceValue);
      };

      const finalFields = fieldsToSubmit.filter(
        (f) => f.fromField !== 'AUTOFILL_CHECKBOX' && isConditionallyVisible(f)
      );

      const fieldValues = await Promise.all(
        finalFields.map(async (f) => {
          let value = f.value;
          if (value instanceof File) {
            const fileType = value.name.split('.').pop() || '';
            try {
              const uploadFileUrl = await firebaseLargeFileUploadHelper({
                blobUrl: URL.createObjectURL(value),
                fileNameWithExtension: value.name,
                storagePath: `creditApplicationsFiles/${
                  editableSelectedTemplate?.supplierId || domainDetails?.id || ''
                }/${Date.now()}`,
              });
              if (uploadFileUrl) {
                value = {
                  name: value.name,
                  url: uploadFileUrl ?? '',
                  fileType: fileType,
                };
              } else {
                throw new Error(`Upload failed for ${f.name}: "${value.name}". Please try again.`);
              }
            } catch (error) {
              Sentry.captureException(error);
              console.error(`❌ Failed to upload ${f.name}: "${value.name}":`, error);
              throw new Error(`Upload failed for ${f.name}: "${value.name}". Please try again.`);
            }
          }

          if (f.isHashed) {
            if (secretKey) {
              const token = CryptoJS.AES.encrypt(JSON.stringify(f.value), secretKey).toString();
              value = token;
            }
          }
          if (f.fromField === 'DATE' && f.value) {
            const formattedDate = moment(f.value).format('MM-DD-YYYY');
            value = formattedDate;
          }
          const fieldData: ConditionalOptionValues[] = [];
          if (f.fieldValue) {
            const fieldValue = await Promise.all(
              f.fieldValue.map(async (fv) => {
                let fvvalue = fv.value;
                if (fvvalue instanceof File) {
                  const fileType = fvvalue.name.split('.').pop() || '';
                  try {
                    const uploadFileUrl = await firebaseLargeFileUploadHelper({
                      blobUrl: URL.createObjectURL(fvvalue),
                      fileNameWithExtension: fvvalue.name,
                      storagePath: `creditApplicationFiles/${
                        editableSelectedTemplate?.supplierId || domainDetails?.id || ''
                      }/${Date.now()}`,
                    });
                    if (uploadFileUrl) {
                      fvvalue = {
                        name: fvvalue.name,
                        url: uploadFileUrl ?? '',
                        fileType: fileType,
                      };
                    } else {
                      throw new Error(
                        `Upload failed for ${f.name}: "${value.name}". Please try again.`
                      );
                    }
                  } catch (error) {
                    Sentry.captureException(error);
                    console.error(`❌ Failed to upload ${f.name}: "${fvvalue.name}":`, error);
                    throw new Error(
                      `Upload failed for ${f.name}: "${fvvalue.name}". Please try again.`
                    );
                  }
                }

                if (fv.isHashed) {
                  if (secretKey) {
                    const token = CryptoJS.AES.encrypt(
                      JSON.stringify(fv.value),
                      secretKey
                    ).toString();
                    fvvalue = token;
                  }
                }
                if (fv.fromField === 'DATE' && fv.value) {
                  const formattedDate = moment(fv.value).format('MM-DD-YYYY');
                  fvvalue = formattedDate;
                }
                return {
                  ...fv,
                  value: fvvalue,
                };
              })
            );
            fieldData.push(...fieldValue);
          }
          return {
            uniqueId: f.uniqueId,
            id: f.id,
            name: f.name,
            fromField: f.fromField,
            isRequired: f.isRequired,
            isHashed: f.isHashed,
            stepName: f.stepName,
            stepNumber: f.stepNumber,
            groupStepPosition: f.groupStepPosition || 0,
            groupedPosition: f.groupedPosition || 0,
            groupName: f.groupName || '',
            groupColumnType: f.groupColumnType || ('ONE_COLUMN' as const),
            isFieldArray: f.isFieldArray || false,
            index: f.index || 0,
            isFieldEmail: f.isFieldEmail || false,
            value,
            ...(f.optionType ? { optionType: f.optionType } : {}),
            ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
            ...(f.secondaryValue ? { secondaryValue: f.secondaryValue } : {}),
            ...(f.status ? { status: f.status } : {}),
            ...(f.emailDate ? { emailDate: f.emailDate } : {}),
            ...(f.sendEmail ? { sendEmail: f.sendEmail } : {}),
            ...(f.type ? { type: f.type } : {}),
            ...(f.fieldValue ? { fieldValue: fieldData } : {}),
            ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
            ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
            ...(f?.linkedFieldId ? { linkedFieldId: f.linkedFieldId } : {}),
            ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
            ...(f.populateFieldSignatureId
              ? { populateFieldSignatureId: f.populateFieldSignatureId }
              : {}),
            ...(f.autoPopulateFieldIds ? { autoPopulateFieldIds: f.autoPopulateFieldIds } : {}),
            ...(f.currencyDecimalPlaces !== undefined
              ? { currencyDecimalPlaces: f.currencyDecimalPlaces }
              : {}),
            ...(f.addressVerification ? { addressVerification: f.addressVerification } : {}),
            ...(f.borderId ? { borderId: f.borderId } : {}),
          };
        })
      );
      let hashedBankReference = bankReference;
      if (bankReference && bankReference.length > 0) {
        hashedBankReference = bankReference.map((bankRef) => {
          const { approvalStatusHistory: hist, ...bankRefWithoutAdminFields } = bankRef as any;
          if (secretKey) {
            const token = CryptoJS.AES.encrypt(
              JSON.stringify(bankRef.bankDetails.accountNumber),
              secretKey
            ).toString();
            return {
              ...bankRefWithoutAdminFields,
              bankDetails: {
                ...bankRef.bankDetails,
                accountNumber: token,
              },
            };
          }
          return bankRefWithoutAdminFields;
        });
      }
      const bankRefField = allStepFieldsValues.find((f) => f.fromField === 'BANK_REFERENCE');
      const isInviteVisible = (sig: StateInvitedUser) => {
        if (!sig.id) return true;
        const inviteField = allStepFieldsValues.find((f) => f.id === sig.id);
        if (!inviteField?.conditionalVisibility?.sourceFieldId) return true;
        const { sourceFieldId, sourceOptionValues } = inviteField.conditionalVisibility;
        const sourceValue = String(
          allStepFieldsValues.find((f) => f.id === sourceFieldId)?.value ?? ''
        );
        return sourceOptionValues.includes(sourceValue);
      };
      let foundInvitedSig = invitedSignatures.filter(
        (sig) => isInviteVisible(sig) && !(isOptionalInvite(sig) && !sig.email?.trim())
      );
      if (
        bankRefField &&
        bankRefField.showIfNotPlaid &&
        bankRefField.linkedFieldId &&
        bankReference?.length
      ) {
        const manualBankRef = bankReference.find((f) => f.connectionType === 'manual');

        if (!manualBankRef) {
          const linkedId = bankRefField.linkedFieldId;

          foundInvitedSig = foundInvitedSig.filter((s) => s?.id && !linkedId.includes(s.id));
        }
      }
      let currentInvitedSignature = await Promise.all(
        invitedSignatures
          .filter((sig) => foundInvitedSig.some((s) => s.placeHolder === sig.placeHolder))
          .map(async (sig) => {
            const fields = sig.signature
              ? await Promise.all(
                  (sig.fields ?? []).map(async (f) => {
                    let value = f.value;
                    if (value instanceof File) {
                      const fileType = value.name.split('.').pop() || '';
                      try {
                        const uploadFileUrl = await firebaseLargeFileUploadHelper({
                          blobUrl: URL.createObjectURL(value),
                          fileNameWithExtension: value.name,
                          storagePath: `creditApplicationFiles/${
                            editableSelectedTemplate?.supplierId || domainDetails?.id || ''
                          }/${Date.now()}`,
                        });
                        if (!uploadFileUrl) {
                          throw new Error(
                            `Upload failed for ${f.name}: "${value.name}". Please try again.`
                          );
                        }
                        value = { name: value.name, url: uploadFileUrl, fileType };
                      } catch (error) {
                        Sentry.captureException(error);
                        console.error(`❌ Failed to upload ${f.name}: "${value.name}":`, error);
                        throw new Error(
                          `Upload failed for ${f.name}: "${value.name}". Please try again.`
                        );
                      }
                    }
                    if (f.isHashed && secretKey) {
                      value = CryptoJS.AES.encrypt(JSON.stringify(value), secretKey).toString();
                    }
                    return { ...f, value };
                  })
                )
              : (sig.fields ?? []).map((f) => ({ ...f, value: '' }));

            return {
              title: sig.title,
              email: sig.email,
              fields,
              signature: sig.signature ?? '',
              dateSigned: sig.dateSigned,
              ipAddress: sig.ipAddress,
              location: sig.location,
              placeHolder: sig.placeHolder ?? '',
              type: sig.type ?? ('AUTHORIZED_AGENT' as const),
              ...(sig.minimumCreditAmount && { minimumCreditAmount: sig.minimumCreditAmount }),
              ...(sig.minimumNumberOfSignatures !== undefined && {
                minimumNumberOfSignatures: sig.minimumNumberOfSignatures,
              }),
              ...(sig?.linkedFieldId && { linkedFieldId: sig.linkedFieldId }),
              ...(sig.signaturePageFieldIds && {
                signaturePageFieldIds: sig.signaturePageFieldIds,
              }),
            };
          })
      );

      const emailField = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value;
      const checkSignatures = foundInvitedSig.filter(
        (s) => s.email !== '' && s.email.trim().toLowerCase() === emailField.trim().toLowerCase()
      );
      if (
        emailField &&
        checkSignatures.length > 0 &&
        dynamicFieldValues &&
        dynamicFieldValues.length > 0
      ) {
        const processesField = await processDynamicFields(
          dynamicFieldValues,
          editableSelectedTemplate?.supplierId ?? ''
        );
        if (!processesField) {
          return {
            data: null,
            message:
              'An unexpected error occurred during file upload or signature submission. Please try again.',
          };
        }
        currentInvitedSignature = processesField as typeof currentInvitedSignature;
      }

      // An invite whose field is required only under a condition that isn't met (e.g. the Personal
      // Guarantor unless Business Type is "LLC (Single-member)") is still sent, but doesn't hold
      // the application in Awaiting Signature (see getSignaturesStatus on the backend).
      currentInvitedSignature = currentInvitedSignature.map((sig) => {
        const source = invitedSignatures.find(
          (item) => item.placeHolder === sig.placeHolder && item.type === sig.type
        );
        return source && isOptionalInvite(source) ? { ...sig, isOptional: true } : sig;
      });

      let details = {};
      if (ipdData) {
        details = {
          ...details,
          metaData: {
            ...ipdData,
          },
        };
      }

      const data = {
        supplierId: editableSelectedTemplate?.supplierId || domainDetails?.id || '',
        templateId: editableSelectedTemplate?.id || '',
        fieldValues,
        invitedSignatures: currentInvitedSignature,
        status: 'AWAITING_SIGNATURE' as const,
        applicationType:
          toCreditApplicationType(editableSelectedTemplate?.applicationType) ||
          ('CREDIT_APPLICATION' as const),
        senderEmail:
          userEmail ||
          senderEmail ||
          allStepFieldsValues
            .find((f) => f.id === 'email-personal')
            ?.value?.toString()
            .trim() ||
          '',
        bankReference: hashedBankReference,
        ...details,
      };
      return { data, message: '' };
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
      return {
        data: null,
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      };
    }
  };

  const getSubmitApplicationId = () => {
    return applicationUpdateIdRef.current || creditApplicationId || creditApplicationIdRef.current;
  };

  const reLogin = async () => {
    const email = allStepFieldsValues.find(
      (f) => f.fromField === 'TEXT' && f.id === 'email-personal'
    )?.value;
    const checkFirebaseUserResponse = await dispatch(
      checkFirebaseUser({ email, supplierName: domainDetails?.name || 'Supplier' })
    );
    if (checkFirebaseUserResponse.type === 'auth/checkFirebaseUser/fulfilled') {
      const payload = checkFirebaseUserResponse.payload as {
        data: {
          emailVerified: boolean;
          metadata: { lastSignInTime: string | null };
          uid: string;
          customToken?: string;
        };
      };
      const { emailVerified } = payload.data;
      if (emailVerified && payload.data?.customToken) {
        await dispatch(
          firebaseSignInWithCustomToken({
            token: payload.data.customToken,
          })
        );
      }
    }
  };

  const handleSubmitCreditApplication = async (
    dynamicFieldValues?: DynamicInvitedSignaturesFields[]
  ) => {
    setIsDisabled(false);
    setLoading(true);

    try {
      const verifiedFields = await verifyAddressFieldsOnSubmit(
        updatedFieldsRef.current || allStepFieldsValues
      );
      setAllStepFieldsValues(verifiedFields);
      setUpdatedFieldsRef(verifiedFields);

      const data = await processData(dynamicFieldValues);
      if (!data.data) {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'ERROR',
            title: 'Error submitting application',
            subTitle: data.message || '',
            onClose: () => {},
          })
        );
        return;
      }
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        await reLogin();
      }

      const applicationId = getSubmitApplicationId();

      const stepData = {
        currentStep: {
          stepName: steps[step],
          stepNumber: Math.min(step + 1, steps.length - 1),
          totalSteps: steps.length - 1,
        },
      };

      const sharedFields = {
        ...(creditApplication?.assignedReviewerUserId && {
          assignedReviewerUserId: creditApplication.assignedReviewerUserId,
        }),
        ...(creditApplication?.amountAproved && {
          amountAproved: creditApplication.amountAproved,
        }),
        ...stepData,
      };

      const submitApplication = async () => {
        const isFromTerminalApp = ['APPROVED', 'REJECTED'].includes(urlAppStatusRef.current ?? '');
        if (isFromTerminalApp && urlSourceApplicationIdRef.current && senderEmail && !isEditMode) {
          return dispatch(
            createApplicationUpdateHistory({
              applicationId: urlSourceApplicationIdRef.current,
              ...data.data,
              status: 'AWAITING_SIGNATURE',
              ...sharedFields,
            })
          );
        }

        if (applicationId) {
          return dispatch(
            updateCreditApplication({
              creditApplicationId: applicationId,
              ...data.data,
              ...sharedFields,
              ...(isEditMode && creditApplication?.status ? { status: 'AWAITING_SIGNATURE' } : {}),
              savingType: 'overwritefield',
            })
          );
        }

        return dispatch(createCreditApplication({ ...data.data, ...sharedFields }));
      };

      const response = await submitApplication();
      const isFulfilled = response?.type.includes('/fulfilled');
      let appAction = senderEmail ? 'Updat' : 'Submitt';
      if (creditApplication && creditApplication.status === 'DRAFT') {
        appAction = 'Submitt';
      }

      if (isFulfilled) {
        setModalState(ModalState.SUCCESS_SAVING_CREDIT_APPLICATION);
        setModalDetails({
          modalType: 'Success',
          title: `${applicationTitle} ${appAction}ed`,
          details: `Your ${applicationTitle.toLowerCase()} has been ${appAction.toLowerCase()}ed successfully.`,
        });
      } else {
        dispatch(
          openMessageModal({
            buttonText: 'Close',
            modalType: 'ERROR',
            title: `Error ${appAction}ing ${applicationTitle}`,
            subTitle: `There was an error ${appAction.toLowerCase()}ing the ${applicationTitle.toLowerCase()}. Please try again later.`,
            onClose: () => {},
          })
        );
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckLogin = async (resendEmail?: boolean): Promise<boolean> => {
    if (step !== 0) return true;

    const email = allStepFieldsValues.find(
      (f) => f.fromField === 'TEXT' && f.id === 'email-personal'
    )?.value;

    if (!email) {
      openDillToast({
        type: 'ERROR',
        message: 'Email is required to check user login.',
      });
      return false;
    }
    setUserEmail(email);
    localStorage.setItem('emailForSignIn', email);
    const firstName =
      allStepFieldsValues.find((f) => f.fromField === 'TEXT' && f.id === 'first-name-personal')
        ?.value || '';
    const lastName =
      allStepFieldsValues.find((f) => f.fromField === 'TEXT' && f.id === 'last-name-personal')
        ?.value || '';

    try {
      const checkFirebaseUserResponse = await dispatch(
        checkFirebaseUser({ email, supplierName: domainDetails?.name || 'Supplier' })
      );
      if (resendEmail) {
        openDillToast({
          type: 'SUCCESS',
          message: 'A verification email has been resent.',
        });
      }

      if (checkFirebaseUserResponse.type === 'auth/checkFirebaseUser/fulfilled') {
        const payload = checkFirebaseUserResponse.payload as {
          data: {
            emailVerified: boolean;
            metadata: { lastSignInTime: string | null };
            uid: string;
            customToken?: string;
            providerData?: { providerId: string }[];
          };
        };

        const { emailVerified, metadata, providerData } = payload.data;
        const lastSignIn = metadata?.lastSignInTime ? new Date(metadata.lastSignInTime) : null;
        const now = new Date();

        const isInactive = !lastSignIn || now.getTime() - lastSignIn.getTime() > 3 * 60 * 60 * 1000;
        const hasPasswordProvider = providerData?.some((p) => p.providerId === 'password') ?? false;

        const emailChangedFromInvite =
          !!senderEmail && email.toLowerCase().trim() !== senderEmail.toLowerCase().trim();

        if (!emailVerified || isInactive || emailChangedFromInvite) {
          const sendLinkResponse = await dispatch(
            unverifyFirebaseUserAndSendLink({
              email,
              domainId: domainDetails?.domainId || '',
              firstName,
              lastName,
              templateId: editableSelectedTemplate?.id || '',
              ...(creditApplicationId ? { applicationId: creditApplicationId } : {}),
              ...(senderEmail ? { senderEmail } : {}),
              ...(salesRepId ? { salesRepId } : {}),
              ...(branchId ? { branchId } : {}),
              ...(hasPasswordProvider && !emailChangedFromInvite ? { skipUnverify: true } : {}),
              ...(resendEmail ? { overrideCooldown: true } : {}),
            })
          );
          if (sendLinkResponse.type !== 'auth/unverifyFirebaseUserAndSendLink/fulfilled') {
            openDillToast({
              type: 'ERROR',
              message: `Failed to send verification link to ${email}.`,
            });
            return false;
          }
          setStepMode('EMAIL_LINK_SENT');
          setIsDisabled(true);
          return false;
        } else if (emailVerified && payload.data?.customToken) {
          await dispatch(
            firebaseSignInWithCustomToken({
              token: payload.data.customToken,
            })
          );

          // Fetch all customer applications (drafts + completed)
          const applicationsResult = await dispatch(
            getCustomerApplications({
              email,
              applicationType: toCreditApplicationType(editableSelectedTemplate?.applicationType),
              isArchived: false,
            })
          );

          if (applicationsResult.type === 'creditApplications/getCustomerApplications/fulfilled') {
            const applications = (applicationsResult as any).payload?.data || [];
            customerApplicationsRef.current = applications;
            setCustomerApplications(applications);
            if (
              applications &&
              applications.length > 0 &&
              !creditApplicationId &&
              !fromUrlParamsRef.current
            ) {
              if (process.env.REACT_APP_ENVIRONMENT === 'production') {
                setStepMode('APPLICATION_DASHBOARD');
                return false;
              }

              // Non-production: skip dashboard and auto-apply the best draft
              const currentSupplierId =
                domainDetails?.id || editableSelectedTemplate?.supplierId || '';
              const byUpdatedAt = (a: CreditApplication, b: CreditApplication) =>
                new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime();

              const supplierDraft = [...applications]
                .filter(
                  (app: CreditApplication) =>
                    app.supplierId === currentSupplierId && app.status === 'DRAFT'
                )
                .sort(byUpdatedAt)[0];

              if (supplierDraft) {
                // Same-supplier draft: continue it directly
                setCreditApplicationId(supplierDraft.id);
                setSenderEmail(supplierDraft.senderEmail);
                setDraftedCreditApplication(supplierDraft);
                setStepMode('STEP_DETAILS');
                setStep(1);
              } else {
                const otherSupplierDraft =
                  [...applications]
                    .filter(
                      (app: CreditApplication) =>
                        app.supplierId !== currentSupplierId && app.status === 'DRAFT'
                    )
                    .sort(byUpdatedAt)[0] ?? null;

                applicationUpdateIdRef.current = null;
                setCreditApplicationId(null);
                setSenderEmail(null);
                // Prefill fieldValue, bankReference and invitedSignatures from other-supplier draft
                setPrefillDraftApplication(otherSupplierDraft);
                setStepMode('STEP_DETAILS');
                setStep(1);
              }

              return false;
            }
          }
        }

        return true;
      }

      const response = await dispatch(
        createCreditAppFirebaseUser({
          email,
          domainId: domainDetails?.domainId || '',
          firstName,
          lastName,
          templateId: editableSelectedTemplate?.id || '',
          ...(creditApplicationId ? { applicationId: creditApplicationId } : {}),
          ...(senderEmail ? { senderEmail } : {}),
          ...(salesRepId ? { salesRepId } : {}),
          ...(branchId ? { branchId } : {}),
        })
      );
      if (response.type !== 'auth/createCreditAppFirebaseUser/fulfilled') {
        openDillToast({
          type: 'ERROR',
          message: `Failed to create user`,
        });
        return false;
      }
      setStepMode('EMAIL_LINK_SENT');
      setIsDisabled(true);

      return false;
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error during Firebase user check:', error);
      return false;
    }
  };

  const handleValidateConditionalSelectedField = () => {
    if (!parentRef.current) {
      setHasConditionalFieldErrors(false);
      return true;
    }
    const refValid = parentRef.current.validate();

    const currentStepFields = allStepFieldsValues
      .filter((f) => f.stepName === steps[step] && f.fieldValue && f.fromField !== 'BANK_REFERENCE')
      .flatMap((f) => f.fieldValue);

    const hasInvalidFields = currentStepFields.some((field) => {
      if (!field) return false;
      if (field.hidden) return false;

      if (
        field.isRequired &&
        ((typeof field.value === 'string' && field.value.trim() === '') || field.value === null)
      ) {
        return true;
      }

      if (
        field.isFieldEmail &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if ((field.emailValidationType ?? 'SINGLE') === 'MULTIPLE') {
          const emails = field.value
            .split(/[,;]/)
            .map((e) => e.trim())
            .filter(Boolean);
          return emails.some((e) => !emailRegex.test(e));
        }
        return !emailRegex.test(field.value);
      }

      if (
        field.validationType === 'EIN' &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        return !/^\d{2}-\d{7}$/.test(field.value.trim());
      }

      if (
        field.validationType === 'SSN' &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        return !/^\d{3}-\d{2}-\d{4}$/.test(field.value.trim());
      }

      if (
        field.validationType === 'PHONE' &&
        field.fromField === 'TEXT' &&
        typeof field.value === 'string' &&
        field.value.trim() !== ''
      ) {
        return !/^\d{3}-\d{3}-\d{4}$/.test(field.value.trim());
      }

      return false;
    });

    const isValid = !hasInvalidFields && refValid !== false;
    setHasConditionalFieldErrors(!isValid);
    return isValid;
  };

  const handleSaveDraft = async () => {
    try {
      if (isEditMode) return;
      if (fromUrlParamsRef.current && !['SENT', 'OPENED'].includes(urlAppStatusRef.current ?? ''))
        return;

      // Only save draft if status is DRAFT, OPENED, or SENT
      if (
        draftCreditApplication &&
        !['DRAFT', 'OPENED', 'SENT'].includes(draftCreditApplication.status)
      ) {
        return;
      }

      const { data } = await processData();
      if (!data) return;

      // handleSaveDraft runs while `step` still points at the step being left, so use the
      // step being advanced to — otherwise the saved progress reflects steps completed
      // rather than the step the applicant is actually on (and may be stuck at).
      const landedStep = Math.min(step + 1, steps.length - 1);
      const stepData = {
        currentStep: {
          stepName: steps[landedStep],
          stepNumber: Math.min(landedStep + 1, steps.length - 1),
          totalSteps: steps.length - 1,
        },
      };
      let details = {};
      if (salesRepId) {
        if (draftCreditApplication && draftCreditApplication?.assignedSalesRep) {
          const assignedSalesRep = new Set(draftCreditApplication?.assignedSalesRep);
          assignedSalesRep.add(salesRepId);
          details = { ...details, assignedSalesRep: Array.from(assignedSalesRep) };
        } else {
          details = { ...details, assignedSalesRep: [salesRepId] };
        }
      }
      if (branchId) {
        details = { ...details, branchId };
      }
      if (draftCreditApplication && draftCreditApplication.isDeleted === true) {
        details = { ...details, isDeleted: false };
      }

      const saveDraft = () =>
        dispatch(
          saveDraftCreditApplication({
            creditApplicationId: creditApplicationId || creditApplicationIdRef.current,
            creditApplicationDetails: {
              ...data,
              ...stepData,
              ...details,
              status: 'DRAFT' as const,
            },
          })
        );

      const result = await saveDraft();

      if (result.type === 'creditApplications/saveDraftCreditApplication/fulfilled') {
        const payload = result.payload as any;
        const id = payload?.data?.id;

        if (id && !creditApplicationIdRef.current) {
          creditApplicationIdRef.current = id;
          setCreditApplicationId(id);
        }
      } else {
        console.error('Failed to save draft:', result.payload);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      console.log('Save draft complete.');
    }
  };

  const handleNextStep = async () => {
    handleValidateConditionalSelectedField();
    validateForm();
    validateInviteSigners();
    if (!handleValidateConditionalSelectedField()) {
      return;
    }

    if (!validateForm()) {
      return;
    }
    if (!validateInviteSigners()) return;

    const senderEmailValue = allStepFieldsValues.find((f) => f.id === 'email-personal')
      ?.value as string;
    if (senderEmailValue) {
      const senderMatchSlots = invitedSignatures.filter(
        (s) =>
          s.step === step &&
          s.email !== '' &&
          s.email.trim().toLowerCase() === senderEmailValue.trim().toLowerCase() &&
          !signedNowPlaceholders.includes(s.placeHolder ?? '')
      );
      if (senderMatchSlots.length > 0) {
        setInvitedSignaturesErrors((prev) =>
          prev.map((err) =>
            senderMatchSlots.some((s) => s.placeHolder === err.placeHolder)
              ? {
                  ...err,
                  errorText: 'This is your email — please use "Sign Now" to sign this slot.',
                }
              : err
          )
        );
        return;
      }
    }

    if (!validateBankReferences()) return;

    setLoading(true);
    const isLoginChecked = await handleCheckLogin();
    if (!isLoginChecked) {
      setLoading(false);
      return;
    }

    if (step > 0 && step < steps.length - 1) {
      const foundBankRef = allStepFieldsValues.find(
        (f) => f.stepName === steps[step] && f.fromField === 'BANK_REFERENCE'
      );

      if (
        bankReference &&
        foundBankRef &&
        foundBankRef.showIfNotPlaid &&
        foundBankRef.linkedFieldId
      ) {
        const linkedId = foundBankRef.linkedFieldId;

        // Example: pick only manual bank reference
        const manualBankRef = bankReference.find((f) => f.connectionType === 'manual');

        if (!manualBankRef) {
          const filteredVal = allStepFieldsValues.filter((f) => !linkedId.includes(f.id));
          setAllStepFieldsValues(filteredVal);
        }
      }
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        await reLogin();
      }
      handleSaveDraft();
    }

    setLoading(false);
    if (step === steps.length - 1) {
      const emailField = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value;

      // 1. Check for duplicate emails within each section first
      const duplicateErrors = invitedSignatures
        .filter((item) => item.email && !signedNowPlaceholders.includes(item.placeHolder ?? ''))
        .filter((item) =>
          invitedSignatures.some(
            (other) =>
              other.placeHolder !== item.placeHolder &&
              other.signatureGroupName === item.signatureGroupName &&
              !signedNowPlaceholders.includes(other.placeHolder ?? '') &&
              other.email !== '' &&
              other.email.trim().toLowerCase() === item.email.trim().toLowerCase()
          )
        );
      if (duplicateErrors.length > 0) {
        setInvitedSignaturesErrors((prev) =>
          prev.map((err) =>
            duplicateErrors.some((d) => d.placeHolder === err.placeHolder)
              ? { ...err, errorText: 'Each signer in this section must have a unique email.' }
              : err
          )
        );
        return;
      }

      // 2. Check if applicant's own email is used without Sign Now
      const checkSignatures = invitedSignatures.filter(
        (s) => s.email !== '' && s.email.trim().toLowerCase() === emailField.trim().toLowerCase()
      );

      const notYetSigned = checkSignatures.filter(
        (s) => !signedNowPlaceholders.includes(s.placeHolder ?? '')
      );
      if (emailField && notYetSigned.length > 0) {
        setInvitedSignaturesErrors((prev) =>
          prev.map((err) =>
            notYetSigned.some((s) => s.placeHolder === err.placeHolder)
              ? {
                  ...err,
                  errorText: 'This is your email — please use "Sign Now" to sign this slot.',
                }
              : err
          )
        );
        return;
      }
      if (emailField && checkSignatures.length > 0 && notYetSigned.length === 0) {
        // All matching slots already signed via Sign Now — proceed to submit
        setInvitedSignaturesErrors((prev) =>
          prev.map((err) =>
            checkSignatures.some((s) => s.placeHolder === err.placeHolder)
              ? { ...err, errorText: '' }
              : err
          )
        );
      }

      const emailList = Array.from(
        new Set(invitedSignatures.map((sign) => sign.email?.toLowerCase().trim()).filter(Boolean))
      );
      const inviteEmails = emailList.filter((email) => email !== emailField?.trim().toLowerCase());
      dispatch(
        openMessageModal({
          modalType: 'INFO',
          title: 'Confirm Submission',
          subTitle: `Are you sure you want to submit this ${applicationTitle.toLowerCase()} request? ${
            inviteEmails.length > 0
              ? `Upon submission, we would send emails to the list of invitees for signing: ${inviteEmails.join(
                  ', '
                )}`
              : ''
          }`,
          buttonText: 'Close',
          isSecondButton: true,
          secondButtonText: 'Submit',
          onSecondButtonClick: async () => {
            handleSubmitCreditApplication();
          },
        })
      );
      return;
    }

    nextStep();
    setErrors([]);
  };

  const handleAddGroupedField = ({
    groupName,
    position,
    count = 1,
  }: {
    groupName: string;
    position: number;
    count?: number;
  }) => {
    if (!editableSelectedTemplate) return;

    const currentStepFields = editableSelectedTemplate.steps[step]?.fields || [];

    const groupedFields =
      currentStepFields.find((f) => f.groupName === groupName && f.position === position)
        ?.groupedFields || [];

    if (!groupedFields) return;

    let newGroupedFields = groupedFields;
    for (let i = 0; i < count; i++) {
      newGroupedFields = duplicateWithNextGroupedPosition(newGroupedFields);
    }
    setEditableSelectedTemplate((prev) => {
      if (!prev) return null;
      const updatedSteps = prev.steps.map((st) => {
        if (st.name === steps[step]) {
          return {
            ...st,
            fields: st.fields.map((f) => {
              if (f.groupName === groupName && f.position === position) {
                return {
                  ...f,
                  groupedFields: newGroupedFields,
                };
              }
              return f;
            }),
          };
        }
        return st;
      });
      return { ...prev, steps: updatedSteps };
    });
  };

  const handleRemoveGroupedField = ({
    groupName,
    position,
    groupPostion,
  }: {
    groupName: string;
    position: number;
    groupPostion: number;
  }) => {
    if (!editableSelectedTemplate) return;

    const currentStepFields = editableSelectedTemplate.steps[step]?.fields || [];

    const templateField = currentStepFields.find(
      (f) => f.groupName === groupName && f.position === position
    );
    const groupedFields = templateField?.groupedFields || [];

    if (!groupedFields) return;

    // Compute the minimum required number of groups
    const specificRules = creditApplication?.specificRules ?? [];
    const groupRule = specificRules.find((r) => r.fieldId === `group:${groupName}`);
    let minimumGroupCount = templateField?.minimunNumberOfGroups ?? 1;
    if (groupRule?.minimumNumberOfGroups !== undefined) {
      minimumGroupCount = groupRule.minimumNumberOfGroups;
    } else if (
      creditApplication?.groupedSpecialConditions &&
      creditApplication.groupedSpecialConditions.length > 0
    ) {
      minimumGroupCount = creditApplication.groupedSpecialConditions[0].minimumNumber;
    }

    // Count distinct groupedPositions currently present
    const uniquePositions = new Set(groupedFields.map((f) => f.groupedPosition)).size;
    if (uniquePositions <= minimumGroupCount) return;

    const newGroupedFields = groupedFields.filter((f) => f.groupedPosition !== groupPostion);

    setEditableSelectedTemplate((prev) => {
      if (!prev) return null;
      const updatedSteps = prev.steps.map((st) => {
        if (st.name === steps[step]) {
          return {
            ...st,
            fields: st.fields.map((f) => {
              if (f.groupName === groupName && f.position === position) {
                return {
                  ...f,
                  groupedFields: newGroupedFields,
                };
              }
              return f;
            }),
          };
        }
        return st;
      });
      return { ...prev, steps: updatedSteps };
    });
  };

  const handleSignNow = (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => {
    setSignature('');
    setSignNowFieldData({
      ...field,
      fields: field.fields.map((f) => ({ ...f, value: '' })),
    });
    setOpenSignNowModal(true);
  };

  const handleSignNowSubmit = (data: {
    dynamicFields: DynamicInvitedSignaturesFields[];
    signature: string;
  }) => {
    if (!signNowFieldData) return;
    const updatedSignatures = invitedSignatures.map((sig) => {
      if (sig.placeHolder !== signNowFieldData.placeHolder) return sig;
      const mergedFields = sig.fields.map((f) => {
        const match = data.dynamicFields.find((pf) => f.id.includes(pf.id));
        return match ? { ...f, value: match.value } : f;
      });
      return {
        ...sig,
        email: signatureDetails.email,
        fields: mergedFields,
        signature: data.signature,
        dateSigned: moment().format('MM-DD-YYYY'),
        ipAddress: ipdData?.ipAddress ?? '',
        location: ipdData?.location ?? '',
      };
    });
    setInvitedSignatures(updatedSignatures);
    setSignedNowPlaceholders((prev) => [...prev, signNowFieldData.placeHolder ?? '']);

    // Now that this slot is signed, the group's minimum-signature requirement may
    // already be satisfied — clear any stale "At least N ... email is required"
    // errors left over on sibling slots in the same group from an earlier validation pass.
    const signedItem = updatedSignatures.find(
      (sig) => sig.placeHolder === signNowFieldData.placeHolder
    );
    if (signedItem) {
      const min = signedItem.minimumNumberOfSignatures;
      const signersCount = updatedSignatures.filter(
        (sig) => sig.type === signedItem.type && sig.email !== ''
      ).length;
      if (min === undefined || signersCount >= min) {
        setInvitedSignaturesErrors((prev) =>
          prev.map((err) => {
            const relatedItem = updatedSignatures.find(
              (sig) => sig.placeHolder === err.placeHolder
            );
            return relatedItem && relatedItem.type === signedItem.type
              ? { ...err, errorText: '' }
              : err;
          })
        );
      }
    }
    setOpenSignNowModal(false);
  };

  const handleEditSignNow = (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => {
    setSignature('');
    setSignNowFieldData(field);
    setOpenSignNowModal(true);
  };

  const handleClearSignNow = (field: {
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  }) => {
    setSignedNowPlaceholders((prev) => prev.filter((p) => p !== field.placeHolder));
    setInvitedSignatures((prev) =>
      prev.map((sig) => {
        if (sig.placeHolder !== field.placeHolder) return sig;
        return {
          ...sig,
          email: '',
          signature: '',
          fields: sig.fields.map((f) => ({ ...f, value: '' })),
        };
      })
    );
  };

  const handleUpdateInviteSigners = (
    data: {
      title: string;
      email: string;
      step: number;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
      minimumNumberOfSignatures?: number;
    }[]
  ) => {
    setInvitedSignatures(data);
    setInvitedSignaturesErrors(
      data.map((item) => {
        if (!item.email || signedNowPlaceholders.includes(item.placeHolder ?? '')) {
          return { placeHolder: item.placeHolder ?? '', errorText: '' };
        }
        const isDuplicate = data.some(
          (other) =>
            other.placeHolder !== item.placeHolder &&
            other.signatureGroupName === item.signatureGroupName &&
            !signedNowPlaceholders.includes(other.placeHolder ?? '') &&
            other.email !== '' &&
            other.email.trim().toLowerCase() === item.email.trim().toLowerCase()
        );
        return {
          placeHolder: item.placeHolder ?? '',
          errorText: isDuplicate ? 'Each signer in this section must have a unique email.' : '',
        };
      })
    );
  };

  const handleUseDifferentEmail = () => {
    setStepMode('STEP_DETAILS');
    setUserEmail('');
    setIsDisabled(false);
    setAllStepFieldsValues((prev) =>
      prev.map((f) => {
        if (f.id === 'email-personal') {
          return { ...f, value: '' };
        }
        return f;
      })
    );
  };

  const handleBankReferenceChange = (
    updater: BankReference[] | null | ((prev: BankReference[] | null) => BankReference[] | null)
  ) => {
    setBankReference((prev) => {
      const updated = typeof updater === 'function' ? updater(prev) : updater;

      const foundBankRef = allStepFieldsValues.find((f) => f.fromField === 'BANK_REFERENCE');

      if (
        foundBankRef &&
        foundBankRef.showIfNotPlaid &&
        foundBankRef.linkedFieldId &&
        updated?.length
      ) {
        const linkedId = foundBankRef.linkedFieldId;

        // Example: pick only manual bank reference
        const manualBankRef = updated.find((f) => f.connectionType === 'manual');

        if (!manualBankRef) {
          const filteredVal = allStepFieldsValues.filter((f) => !linkedId.includes(f.id));
          setAllStepFieldsValues(filteredVal);
        }
      }

      return updated;
    });
  };

  const amountOfCredit = useMemo(() => {
    const amount = allStepFieldsValues.find((f) => f.id.includes('amount-of'))?.value;
    return amount && typeof amount === 'number' ? amount : undefined;
  }, [allStepFieldsValues]);

  const handleContinueApplication = (application: CreditApplication) => {
    const { status, supplierId } = application;
    setIsEditMode(false);
    fromUrlParamsRef.current = false;

    // Non-editable statuses — just navigate to form without loading any application
    if (!['SENT', 'OPENED', 'DRAFT'].includes(status)) {
      setStepMode('STEP_DETAILS');
      setStep(1);
      return;
    }

    applicationUpdateIdRef.current = application.id;

    if (status === 'DRAFT') {
      setCreditApplicationId(application.id);
      setSenderEmail(application.senderEmail);
      setStepMode('STEP_DETAILS');
      setStep(1);
      return;
    }

    const latestDraft = [...customerApplications]
      .filter((a) => a.supplierId === supplierId && a.status === 'DRAFT')
      .sort(
        (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
      )[0];

    if (status === 'SENT') {
      dispatch(
        updateCreditApplication({
          creditApplicationId: application.id,
          status: 'OPENED',
        })
      );
    }

    if (!latestDraft) {
      setCreditApplicationId(application.id);
      setSenderEmail(application.senderEmail);
      setStepMode('STEP_DETAILS');
      setStep(1);
      return;
    }

    setCreditApplicationId(latestDraft.id);
    setSenderEmail(latestDraft.senderEmail);
    setStepMode('STEP_DETAILS');
    setStep(1);
    return;
  };

  const handleEditApplication = async (application: CreditApplication) => {
    try {
      setIsEditMode(true);
      fromUrlParamsRef.current = false;
      setLoading(true);
      const result = await dispatch(
        getCreditApplication({ creditApplicationId: application.id, hashedType: 'encrypted' })
      );
      if (result.type === 'creditApplications/getCreditApplication/fulfilled') {
        const app = (result as any).payload?.data;
        if (app) {
          setCreditApplicationId(application.id);
          setSenderEmail(app.senderEmail);
          setStepMode('STEP_DETAILS');
          setStep(1);
        }
      }
    } catch (error) {
      console.error('Error loading application:', error);
      openDillToast({
        type: 'ERROR',
        message: 'Failed to load application. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewApplication = () => {
    const currentSupplierId = domainDetails?.id || editableSelectedTemplate?.supplierId || '';
    const existingSupplierDraft = customerApplications.find(
      (app) => app.supplierId === currentSupplierId && app.status === 'DRAFT'
    );

    if (existingSupplierDraft) {
      handleContinueApplication(existingSupplierDraft);
      return;
    }

    applicationUpdateIdRef.current = null;
    setCreditApplicationId(null);
    setSenderEmail(null);
    setStepMode('STEP_DETAILS');
    setStep(1);

    const otherSupplierDraft =
      [...customerApplications]
        .filter((app) => app.supplierId !== currentSupplierId && app.status === 'DRAFT')
        .sort(
          (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime()
        )[0] ?? null;

    setPrefillDraftApplication(otherSupplierDraft);
  };

  const handleSignCreditApplication = (data: {
    dynamicFields: DynamicInvitedSignaturesFields[];
    signature: string;
  }) => {
    const filteredInviteSig = allStepFieldsValues
      .filter((val) => val.fromField === 'INVITE_USER' && val?.linkedFieldId)
      .map((val) => {
        return val.linkedFieldId;
      });
    const htmlVal = data.dynamicFields.reduce<Record<string, string>>((acc, fld) => {
      acc[fld?.id ?? ''] = fld.value;
      return acc;
    }, {});
    const updatedHtmlVal = {
      ...htmlVal,
      signature: data.signature
        ? `<img class="h-[20px] w-auto" src=${data.signature} alt="" />`
        : '',
      'signed-date': data.signature ? moment().format('MM-DD-YYYY') : '',
    };
    const updatedFields = allStepFieldsValues.map((val) => {
      if (filteredInviteSig.includes(val.uniqueId)) {
        const dataVal = renderTemplate(val?.formTemplate ?? '', updatedHtmlVal);
        return {
          ...val,
          formTemplate: dataVal,
        };
      }
      return val;
    });
    const baseInvitedSignatures = invitedSignatures.map((sig) => ({
      ...sig,
      title: sig.title,
      email: sig.email,
      fields: sig.fields ?? [],
      signature: '',
      placeHolder: sig.placeHolder ?? '',
      type: sig.type ?? ('AUTHORIZED_AGENT' as const),
      ...(sig.minimumCreditAmount && { minimumCreditAmount: sig.minimumCreditAmount }),
      ...(sig.minimumNumberOfSignatures !== undefined && {
        minimumNumberOfSignatures: sig.minimumNumberOfSignatures,
      }),
      ...(sig?.linkedFieldId && { linkedFieldId: sig.linkedFieldId }),
    }));

    const email = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value ?? '';

    const updatedSignatures = baseInvitedSignatures.map((sig) => {
      if (sig.email.trim().toLowerCase() !== email.trim().toLowerCase()) return sig;

      const mergedFields = sig.fields.map((field) => {
        const match = data.dynamicFields.find((pf) => field.id.includes(pf.id));
        return match ? { ...field, id: match.id, value: match.value } : field;
      });

      return {
        ...sig,
        fields: mergedFields,
      };
    });

    setInvitedSignatures(updatedSignatures);

    setAllStepFieldsValues(updatedFields);
    setUpdatedFieldsRef(updatedFields);

    const emailField = allStepFieldsValues.find((f) => f.id === 'email-personal')?.value;
    const emailList = Array.from(
      new Set(invitedSignatures.map((sign) => sign.email?.toLowerCase().trim()).filter(Boolean))
    );
    const inviteEmails = emailList.filter((ema) => ema !== emailField?.trim().toLowerCase());

    dispatch(
      openMessageModal({
        modalType: 'INFO',
        title: 'Confirm Submission',
        subTitle: `Are you sure you want to submit this ${applicationTitle.toLowerCase()} request? ${
          inviteEmails.length > 0
            ? `Upon submission, we would send emails to the list of invitees for signing: ${inviteEmails.join(
                ', '
              )}`
            : ''
        }`,
        buttonText: 'Close',
        isSecondButton: true,
        secondButtonText: 'Submit',
        onSecondButtonClick: async () => {
          handleSubmitCreditApplication(data.dynamicFields);
          setOpenCustomerSignatureModal(false);
        },
      })
    );
  };

  return (
    <div className="w-full flex justify-center items-center p-1 md:p-6 bg-gray-100">
      <CreateSignatureModal
        open={signatureModalOpen}
        handleClose={() => setSignatureModalOpen(false)}
        addSignature={(sign) => {
          setSignature(sign);
          setSignatureModalOpen(false);
        }}
        defaultIsManualSign={false}
        onDownloadRealease={() => {}}
        subText="Please sign the credit application to proceed."
        disableTypedSignature={basicSupplier?.isCreditAppTypedSignatureDisabled ?? false}
      />
      <CustomerSignatureModal
        open={openCustomerSignatureModal}
        handleClose={() => setOpenCustomerSignatureModal(false)}
        email={signatureDetails.email}
        invitedSignature={signatureDetails.invitedSignatures}
        signature={signature}
        onOpenSignature={() => setSignatureModalOpen(true)}
        onSignCreditApplication={handleSignCreditApplication}
      />
      <CustomerSignatureModal
        open={openSignNowModal}
        handleClose={() => setOpenSignNowModal(false)}
        email={signatureDetails.email}
        invitedSignature={signNowInvitedSignatures}
        signature={signature}
        onOpenSignature={() => setSignatureModalOpen(true)}
        onSignCreditApplication={handleSignNowSubmit}
      />
      {stepMode === 'EMAIL_LINK_SENT' && (
        <div className="flex flex-col items-center justify-center w-full">
          <div className="w-full flex flex-col items-center px-4">
            {/* Progressbar */}
            <ProgressStepBar currentStep={step} steps={steps} />
          </div>
          <div className="flex flex-col w-[calc(100%-20px)] md:w-[412px] justify-center items-center mt-4">
            {domainDetails?.url && (
              <div className="flex flex-col items-center mb-6 md:mb-14">
                <img className="max-h-14 md:max-h-20 object-contain" src={domainDetails.url} />
                <div className="flex items-center mt-2">
                  <p className="text-xs md:text-sm">Powered By</p>
                  <a href="https://usedill.com/" target="_blank">
                    <img className="h-8 md:h-12 ml-2" src={dillLogoWithText} />
                  </a>
                </div>
              </div>
            )}
            {!domainDetails?.url && (
              <div className="flex flex-col items-center mb-14 w-full">
                <img className="w-8/12" src={dillLogoWithText} />
              </div>
            )}
            <div className="flex flex-col bg-white rounded-lg p-6 shadow-md">
              <p className="text-center font-bold text-sm">
                Check your email for a verification link to sign in to your portal. *Make sure to
                open the link from this browser.
              </p>
              <p className="text-center text-sm mt-6">
                Didn’t receive the link?{' '}
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer"
                  onClick={() => handleCheckLogin(true)}>
                  Resend here
                </span>
              </p>
              <p className="text-center text-sm mt-6">
                If you're still not receiving the link, check with your IT department to whitelist
                the domain <span className="PRIMARY_500-CLR ">usedill.com</span>
              </p>
              <p className="text-center mt-4">
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                  onClick={handleUseDifferentEmail}>
                  Use a different email
                </span>
              </p>
              <p className="text-2xs text-center">
                Current email: <span className="PRIMARY_500-CLR ">{userEmail}</span>
              </p>
            </div>
          </div>
        </div>
      )}
      {stepMode === 'APPLICATION_DASHBOARD' && (
        <CustomerApplicationDashboard
          email={userEmail}
          applications={customerApplications as any}
          onContinueApplication={handleContinueApplication}
          onEditApplication={handleEditApplication}
          onStartNewApplication={handleStartNewApplication}
          currentSupplierId={domainDetails?.id || editableSelectedTemplate?.supplierId || ''}
          currentSupplierName={domainDetails?.name || ''}
          onBack={() => setStepMode('STEP_DETAILS')}
        />
      )}
      {stepMode === 'STEP_DETAILS' && (
        <div className="w-full flex flex-col items-center">
          {/* Progressbar */}
          {step !== 0 && (
            <div className="w-full flex flex-col items-center px-1 mb-1 md:px-4 md:mb-4">
              <ProgressStepBar currentStep={step} steps={steps} />
            </div>
          )}

          {/* Form Steps */}
          {modalState === ModalState.ADD_CREDIT_APPLICATION && (
            <div className="w-full md:w-5/6 lg:w-3/4">
              {editableSelectedTemplate && (
                <div className="w-full mt-3 md:mt-6 WHITE-BG rounded-lg p-2 md:p-6">
                  {step === 0 && (
                    <div className="mb-2 GREY_500-CLR text-xs">
                      Email verification helps us to reduce fraudulent applications
                    </div>
                  )}
                  {step === steps.length - 1 ? (
                    <div>
                      <h2 className="text-base md:text-lg font-semibold mb-4">Review & Submit</h2>
                      <PreviewCreditApplicationData
                        formData={allStepFieldsValues}
                        bankReference={bankReference}
                        isRequest={true}
                        inviteSignatures={invitedSignatures.map((item) => ({
                          ...item,
                          signature: item.signature ?? '',
                          placeHolder: item.placeHolder ?? '',
                        }))}
                        ref={previewRef}
                      />
                    </div>
                  ) : (
                    <DynamicStepForm
                      ref={parentRef}
                      title={editableSelectedTemplate.steps[step]?.name}
                      stepFields={editableSelectedTemplate.steps[step]?.fields || []}
                      formData={allStepFieldsValues}
                      onChange={onChangeFormData}
                      enableAutofillCheckboxes
                      errors={errors}
                      onAddGroupedField={handleAddGroupedField}
                      onRemoveGroupedField={handleRemoveGroupedField}
                      onUpdateInviteSigners={handleUpdateInviteSigners}
                      invitedSignatures={invitedSignatures}
                      invitedSignaturesErrors={invitedSignaturesErrors}
                      bankReference={bankReference}
                      onUpdateBankReference={handleBankReferenceChange}
                      blockedBankReferenceEmailDomains={blockedBankReferenceEmailDomains}
                      bankError={bankError}
                      bankReferenceRequiredFields={
                        editableSelectedTemplate?.bankReferenceRequiredFields || []
                      }
                      amountOfCredit={amountOfCredit}
                      domainDetails={domainDetails}
                      step={step}
                      // No credit / COD picker on an external form.
                      templates={
                        selectedTemplate?.applicationType === 'EXTERNAL_FORM'
                          ? []
                          : availableTemplates
                      }
                      selectedTemplate={selectedTemplate}
                      onSelectTemplate={handleTemplateSelect}
                      onSignNow={handleSignNow}
                      onEditSignNow={handleEditSignNow}
                      onClearSignNow={handleClearSignNow}
                      signedNowPlaceholders={signedNowPlaceholders}
                      senderEmail={signatureDetails.email}
                    />
                  )}
                  {step === 0 && (
                    <div className="mb-2 GREY_500-CLR text-xs">
                      By continuing, you agree to the{' '}
                      <a
                        href="https://www.usedill.com/terms"
                        target="_blank"
                        className="PRIMARY_500-CLR underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://www.usedill.com/privacy"
                        target="_blank"
                        className="PRIMARY_500-CLR underline">
                        Privacy Policy
                      </a>
                    </div>
                  )}
                  <StepFormButtons
                    step={step}
                    minStep={0}
                    maxStep={steps.length - 1}
                    onPrevious={prevStep}
                    onContinue={handleNextStep}
                    isLoading={loading}
                    isDisabled={isDisabled}
                    error={
                      errors.length > 0 ||
                      invitedSignaturesErrors.some((e) => e.errorText !== '') ||
                      !!bankError ||
                      hasConditionalFieldErrors
                        ? 'Please fill in all required fields before continuing.'
                        : null
                    }
                  />
                </div>
              )}
            </div>
          )}
          {modalState === ModalState.SUCCESS_SAVING_CREDIT_APPLICATION && (
            <JobSavingSuccess
              type={modalDetails.modalType}
              details={modalDetails.details}
              title={modalDetails.title}
            />
          )}
        </div>
      )}
    </div>
  );
};
